// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
export const enOrgCalendar = {
    heading: 'Working days and holidays',
    effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
    no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
    working_days_legend: 'Working days of the week',
    working_days_hint: 'Working-day deadlines count only these days.',
    working_days_required: 'Select at least one working day.',
    day_0: 'Sunday',
    day_1: 'Monday',
    day_2: 'Tuesday',
    day_3: 'Wednesday',
    day_4: 'Thursday',
    day_5: 'Friday',
    day_6: 'Saturday',
    holidays_heading: 'Holidays',
    holidays_caption: 'Holidays entered: {count}',
    empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
    add_legend: 'Add a holiday',
    date_label: 'Date',
    label_label: 'Label (optional)',
    label_hint: 'A short name for the day, for example why the office is closed.',
    add: 'Add date',
    remove: 'Remove',
    remove_aria: 'Remove {date}',
    col_date: 'Date',
    col_label: 'Label',
    col_actions: 'Actions',
    no_label: 'No label',
    date_required: 'Enter a date.',
    date_out_of_range: 'Enter a date between {min} and {max}.',
    duplicate: '{date} is already in the list.',
    limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
    save: 'Save calendar',
    saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
    save_error: 'The calendar was not saved. Your entries are still here.',
    conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
    reload: 'Reload calendar',
    load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
    retry: 'Try again',
    last_saved: 'Last saved {date}.',
    never_saved: 'This calendar has not been saved here yet.',
    read_only: 'You can view this calendar but not change it.',
    unsaved: 'Unsaved changes.',
    holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
    extra_heading: 'Make-up working days',
    extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
    extra_caption: 'Make-up working days entered: {count}',
    extra_empty: 'No make-up working days entered.',
    extra_add_legend: 'Add a make-up working day',
    extra_date_label: 'Date worked',
    extra_label_label: 'Label for the day worked (optional)',
    extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
    extra_add: 'Add day worked',
    extra_remove_aria: 'Remove make-up working day {date}',
    extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
    extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
    extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
    extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
};
export const enEscalationLadder = {
    heading: 'Escalation ladder',
    intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
    off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
    on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
    suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
    caption: 'Escalation tiers: {count}',
    col_threshold: 'Share of period',
    col_name: 'Name',
    col_action: 'Action',
    col_targets: 'Notifies',
    col_channels: 'Sent by',
    col_actions: 'Actions',
    action_notify: 'Notify',
    action_escalate_to_role: 'Escalate to a role',
    action_reassign: 'Reassign',
    channel_in_app: 'In the platform',
    channel_email: 'Email',
    target_assignee: 'The assignee',
    target_role: 'Holders of {role}',
    target_users: 'Named: {names}',
    reassign_to: 'Moves the case to: {target}',
    threshold_breach: '100% (due date passed)',
    inactive: 'Inactive',
    edit: 'Edit',
    edit_aria: 'Edit the tier at {pct}%',
    remove: 'Remove',
    remove_aria: 'Remove the tier at {pct}%',
    add: 'Add tier',
    add_legend: 'Add a tier',
    edit_legend: 'Edit the tier at {pct}%',
    threshold_label: 'Share of the period elapsed (%)',
    threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
    name_label: 'Name (optional)',
    name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
    action_label: 'Action',
    action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
    role_label: 'Role',
    role_none: 'No role',
    users_label: 'Named people',
    users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
    user_select_label: 'Person to add',
    user_placeholder: 'Choose a person',
    user_add: 'Add person',
    user_remove_aria: 'Remove {name}',
    users_empty: 'No one named.',
    users_retry_subject: 'the list of people',
    channels_legend: 'Send by',
    channels_hint: 'Each person still receives only what their own notification preferences allow.',
    active_label: 'Active',
    apply: 'Apply to the ladder',
    cancel: 'Cancel',
    save: 'Save escalation ladder',
    saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
    unsaved: 'Unsaved changes.',
    save_error: 'The escalation ladder was not saved. Your changes are kept.',
    conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
    reload: 'Reload ladder',
    load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
    retry: 'Try again',
    read_only: 'You can view the escalation ladder but not change it.',
    empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
    last_saved: 'Last saved {date}.',
    err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
    err_too_many_tiers: 'A ladder holds at most {max} tiers.',
    err_invalid_value: 'Tier {n}: a value is not valid.',
    err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
    err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
    err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
    err_channels_required: 'Tier {n}: choose at least one way to send.',
    err_target_role_required: 'Tier {n}: choose the role to escalate to.',
    err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
    err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
    err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
};
export const enModuleTips = {
    show_guide: 'Show guide',
    boundary_show_statements: 'Show statements',
    boundary_hide_statements: 'Hide statements',
    boundary_summary: '{count} statement(s) from {frameworks} installed framework(s)',
    hide_guide: 'Hide guide',
    title: 'Module Guide',
    title_prefix: 'Module tip:',
    loading_aria: 'Loading module tip',
    dismiss: 'Close the Module Guide',
    related_frameworks: 'Related frameworks',
    quick_start_label: 'Quick start',
    related_forms_note: 'Related compliance forms are available below — look for the Mẫu số badge to identify official Vietnamese regulatory forms.',
    read_full_guide: 'Read full guide',
    view_full_requirements: 'View full requirements',
    relevant_for: 'Relevant for',
    key_procedure: 'Key procedure',
    deletion_orchestration: 'Coordinate data deletion requests across systems and third-party vendors with verification workflows and audit trails.',
    // fix-sprint #431 — named portability as a required right. It lists the
    // rights as the help does: the request types your installed frameworks state
    // a period for, and no instrument named here.
    rights_requests: 'Manage data subject rights requests: access, correction, deletion, restriction, objection, consent withdrawal and protective measures, as your installed frameworks provide for them. Where an installed framework states the periods for rights requests, a request type it states no period for is refused when the case is created.',
    consent: 'Manage consent collection policies and workflows. Required for PDPL, E-Commerce Law, and GDPR compliance.',
    consent_properties: 'Configure consent collection points and CMP integration properties for web and app consent tracking.',
    data_mapping: 'Map data flows across your organization — processing activities, systems, and cross-border transfers.',
    data_mapping_registry: 'Maintain a per-field inventory of every personal-data element your organization holds. Attach each element to a Data Category, override sensitivity or retention when needed, and import from regulatory register submissions (PDPL personal-data inventory) without losing operator edits.',
    data_mapping_ropa_completeness: 'Per-field hygiene audit of every Processing Activity against the framework’s required ROPA fields (PDPL, GDPR, Decree 165). Identify critical / high / conditional misses before regulatory submission and jump straight into the activity editor to fix each one.',
    assessments: 'Create and manage Data Protection Impact Assessments (DPIAs) and AI system risk assessments. Required under PDPL Law 91/2025 Article 24.',
    classification: 'Classify and label data assets by sensitivity, category, and regulatory requirements.',
    incidents: 'Record and manage security incidents and data breaches. Required for breach notification under PDPL and Cybersecurity Law.',
    incident_ops: 'Coordinate incident response operations with playbook activations, escalation paths, and real-time coordination dashboards.',
    monitoring: 'Monitor compliance KPIs, program health, and task completion rates across your organization.',
    vendor_risk: 'Assess and manage vendor risk through questionnaires, risk portfolios, and compliance scoring.',
    governance: 'Manage vendor governance, playbooks, and third-party compliance requirements.',
    program_governance: 'Configure program governance maturity assessments, dashboard metrics, and compliance scoring frameworks.',
    discovery: 'Discover and inventory data assets across your organization with automated scanning and staging workflows.',
    discovery_cycles: 'Schedule and run recurring discovery cycles that re-scan systems, surface delta changes, and route findings through review, approval, and publishing.',
    connectors: 'Configure integrations and data connectors for automated compliance data collection.',
    task_management: 'Manage compliance tasks, assignments, and deadlines across teams and modules.',
    audit_trail: 'View and search the unified audit trail for all compliance activities and evidence collection.',
    evidence_packs: 'Assemble, verify, and submit audit evidence packs for regulatory compliance. Evidence packs collect audit events by framework, entity type, and time period into exportable bundles for internal review or regulatory submission.',
    audit_periods: 'Define and manage audit reporting periods per regulatory framework. Audit periods set the time boundaries and retention policies for evidence collection and compliance reporting cycles.',
    qs_evidence_pack: 'Assemble your first evidence pack',
    qs_audit_period: 'Create an audit reporting period',
    compliance_forms: 'Browse the compliance form catalog and create form instances for regulatory filings.',
    sector_overlays: 'Install the optional sector overlays your organisation is subject to, choose Direct or Reference only for each, and work in the sector registers an overlay installed Direct brings into use, grouped by sector under Sector Overlays. Available in the Enterprise package.',
    sector_overlays_overview: 'Sector Overlays › Overlay Management › Overview: the sector registers your active overlays bring into use, grouped by sector, with the overlays that provide each group and the shared views each sector uses elsewhere in the menu. A sector group appears here and in the Sector Overlays menu only while an overlay that declares it is installed Direct; an overlay installed Reference only adds no views. Install overlays and change their mode in Sector Overlays › Overlay Management › Overlay Catalog.',
    partner_workspace: 'Manage partner and reseller workspaces with scoped access, compliance review workflows, and collaboration tools.',
    localization: 'Manage regulatory frameworks, language packs, and localization settings.',
    onboarding: 'Track onboarding progress, complete quick starts, and follow persona-specific compliance paths.',
    organizations: 'Manage organization structure, departments, and multi-org configurations.',
    privacy_scanner: 'Monitor website privacy compliance with automated scanning and issue detection.',
    access_accountability: 'Configure access review campaigns and accountability policies for data access governance.',
    dashboard: 'View the compliance dashboard overview with real-time status summaries, framework coverage, and key compliance metrics.',
    platform: 'Manage platform security settings including API keys, break-glass emergency access, and SSO configuration.',
    framework_vn_pdpl: 'VN Personal Data Protection Law (PDPL)',
    framework_vn_cybersecurity: 'VN Cybersecurity Law',
    framework_vn_ecommerce: 'VN E-Commerce Law',
    framework_vn_ai_law: 'VN AI Law',
    framework_vn_telecom: 'VN Telecom Law',
    framework_vn_banking_circular_83: 'VN Banking Circular 83/2025/TT-NHNN — Internal Control System',
    framework_gdpr: 'EU GDPR',
    cross_framework: 'Cross-framework — supports all regulatory compliance activities',
    qs_dpia_filing: 'Complete your first DPIA filing',
    qs_dept_compliance: 'Submit your first department compliance section',
    qs_ecommerce_consent: 'Prepare your e-commerce platform registration dossier',
    qs_telecom_data: 'Set up telecom subscriber data handling',
    qs_data_flow: 'Map your first data flow',
    qs_incident_response: 'Configure incident response workflow',
    qs_ai_risk: 'Run an AI system risk assessment',
    qs_cross_border: 'Assess a cross-border data transfer',
    qs_breach_notification: 'Simulate a breach notification workflow',
    qs_audit_trail: 'Review a compliance audit trail',
    procedure_data_deletion_fulfillment: 'Data Deletion Fulfillment',
    procedure_cross_system_deletion: 'Cross-System Deletion',
    procedure_dsar_intake: 'Data Subject Access Request Intake',
    procedure_cmp_property_config: 'CMP Property Configuration',
    procedure_consent_collection: 'Consent Collection Policy',
    procedure_ropa_filing: 'Record of Processing Activities Filing',
    procedure_cross_border_assessment: 'Cross-Border Data Transfer Assessment',
    procedure_data_classification: 'Data Classification',
    procedure_breach_notification: 'Breach Notification Process',
    procedure_incident_timeline: 'Incident Timeline Tracking',
    procedure_playbook_escalation: 'Playbook Escalation',
    procedure_compliance_kpi_monitoring: 'Compliance KPI Monitoring',
    procedure_vendor_risk_assessment: 'Vendor Risk Assessment',
    procedure_transfer_mechanism_review: 'Transfer Mechanism Review',
    procedure_program_maturity: 'Program Maturity Assessment',
    procedure_data_discovery: 'Data Asset Discovery',
    procedure_connector_setup: 'Connector Setup and Configuration',
    procedure_task_assignment: 'Task Assignment and Delegation',
    procedure_audit_evidence: 'Audit Evidence Collection',
    procedure_form_submission: 'Compliance Form Submission',
    procedure_partner_onboarding: 'Partner Onboarding Workflow',
    procedure_partner_access: 'Partner Access and Permissions',
    procedure_framework_pack_install: 'Framework Pack Installation',
    procedure_language_pack_deploy: 'Language Pack Deployment',
    procedure_org_onboarding: 'Organization Onboarding Setup',
    procedure_multi_org_config: 'Multi-Organization Configuration',
    procedure_privacy_scan: 'Privacy Compliance Scan',
    procedure_access_review: 'Access Review Campaign',
    procedure_dpia_filing: 'DPIA Filing Process',
    security: 'Platform security configuration: API key management, session policies, MFA enforcement, SSO integration, IP allowlists, and break-glass emergency access controls.',
    settings: 'Platform-wide configuration: notification preferences, domain management, export schedules, and module configuration for your organization.',
    users: 'User account management: create and manage user accounts, assign roles, configure department membership, and track login activity.',
    roles: 'Role and permission management: define custom roles, configure granular permissions per module, and enforce separation of duties policies.',
    profile: 'Your personal profile settings: display name, contact details, language preference, module guide preferences, accessibility options, and password management.',
    consent_properties_bindings: 'Manage consent policy bindings across digital properties. Track which policy version is deployed to each property, identify outdated bindings, and coordinate policy rollouts.',
    consent_properties_templates: 'Golden template management: define standard consent configurations, compare properties against templates, and roll out template changes across property groups.',
    consent_properties_compliance: 'Consent compliance monitoring: detect configuration inconsistencies between properties and golden templates, manage change requests through approval workflows, and generate evidence packs for audit readiness.',
    consent_properties_operations: 'Operational management for consent properties: configure automated snapshot schedules, organize properties into groups, and review the consent property audit trail.',
    consent_properties_audit: 'Consent property audit and analytics: review configuration change history, analyze property compliance trends, and generate compliance reports for regulatory review.',
    classification_taxonomies: 'Define and manage data classification taxonomies with sensitivity and importance dimensions. Configure classification levels, handling requirements, and AI risk tiers.',
    classification_labeling: 'Apply classification labels to data assets. Submit labels for review, manage bulk classification, and track labeling status across your data inventory.',
    classification_lifecycle: 'Track the lifecycle of classified data assets through stages: draft, active use, under review, archival, and pending deletion. Monitor retention periods and trigger deletion workflows.',
    classification_gaps: 'Detect and manage classification gaps across your data inventory. Run gap detection to identify unlabeled assets and missing lifecycle assignments, then track remediation.',
    classification_reviews: 'Manage classification review tasks with escalation timers. Review proposed labels, handle exceptions, and ensure timely classification decisions.',
    incidents_breach: 'Manage data breach notifications and authority reporting. Create notification packages, track regulatory deadlines, and document the complete breach response timeline.',
    incidents_authority: 'Handle formal authority requests from regulatory bodies. Track incoming inquiries, assemble evidence packages, manage verification checklists, and meet response deadlines.',
    incidents_disclosure: 'Maintain the centralized disclosure register for all communications with regulatory authorities. Record disclosure dates, channels, recipients, and content summaries for audit trail compliance.',
    incidents_reports: 'Generate and manage incident reports for internal review and regulatory submission. Create detailed incident summaries, track report status, and export documentation for authority filings.',
    incidents_tasks: 'Coordinate incident response tasks across teams. Create task checklists, assign owners, track priorities and dependencies, escalate blocked items, and monitor completion progress.',
    incidents_supplements: 'Manage supplementary documentation requested by regulatory authorities after initial breach notification. Track supplement requests, prepare response packages, and monitor submission deadlines.',
    incidents_remediation: 'Track corrective actions assigned to departments and users to address the root cause and impact of incidents. Monitor implementation deadlines, verify completion, and document remediation evidence.',
    incident_ops_playbooks: 'Create and manage incident response playbooks. Define escalation paths, assign response teams, and activate playbooks during live incidents.',
    incident_ops_sla: 'Track incident response SLAs and regulatory deadlines. Monitor compliance with statutory notification timelines and measure response performance metrics.',
    incident_ops_evidence: 'Maintain incident evidence chains and conduct post-incident reviews. Generate regulator-ready export packages with complete incident documentation.',
    data_mapping_systems: 'Register and manage systems that process personal data. Document system ownership, data categories, processing purposes, and compliance scoring.',
    data_mapping_flows: 'Map data flows between systems, including cross-border transfers. Document data movement paths, transfer mechanisms, and recipient jurisdictions.',
    data_mapping_activities: 'Document processing activities and generate Records of Processing Activities (RoPA). Link activities to legal bases, purposes, and data categories.',
    data_mapping_governance: 'Manage data governance assignments, attestation cycles, and risk assessments. Track governance ownership and review schedules across your data inventory.',
    data_mapping_reference: 'Manage reference data for data mapping: data categories, processing purposes, retention policies, and export configurations.',
    data_mapping_categories: 'Manage the data category catalog: names, sensitivity tiers, examples, and tags. Foundation for every classification and mapping activity.',
    data_mapping_purposes: 'Catalog processing purposes with their legal basis. Used by ROPA, DPIA, and lawfulness audits.',
    data_mapping_retention: 'Configure retention policies, disposal methods, and review cadence. Aligns with Decree 13/2023 and Decree 356/2025 requirements.',
    data_mapping_exports: 'Export ROPA, systems, purposes, and categories to CSV, XLSX, PDF, or DOCX. All exports land in the platform File Browser.',
    data_mapping_attestation: 'Schedule and monitor data attestation cycles. Track attestation completion across your data inventory.',
    data_mapping_review: 'Classification review queue: review tasks, quality signals, and approval status.',
    data_mapping_risk: 'Risk assessment for processing activities: scoring frameworks, mitigation plans, and effectiveness tracking.',
    data_mapping_evidence: 'Evidence tied to processing activities: security certificates, DPA contracts, DPIA reports.',
    data_mapping_inventory: 'Classification inventory overview: tags, sensitivity tiers, and organisation-wide distribution.',
    data_mapping_exceptions: 'Exception register for classifications: rationale, approver, validity window, and remediation path.',
    data_mapping_history: 'Classification change history: who changed what, when, and why.',
    access_accountability_logs: 'Real-time stream of privileged access events. Pair with the policies page to define who can reach each data zone; pair with investigations to follow up on anomalies.',
    access_accountability_policies: 'Access-zone policy authoring: declare which roles and users may reach each data zone, with time-bounded exceptions. Drives the enforcement checks that surface in the logs.',
    access_accountability_anchoring: 'Tamper-evident anchoring of the access log to a hash chain. Required for VN Cybersecurity Law audit trail integrity.',
    access_accountability_investigations: 'Case workspace for investigating suspicious access events. Timeline, linked logs, evidence packs, and case closure workflow in one view.',
    assessment_triggers: 'Rules that auto-open a DPIA or dossier filing when upstream changes (new processing activity, new vendor, new cross-border flow) meet the trigger condition.',
    connectors_gap_register: 'Catalog of unimplemented or partial connectors. Triage items, assign owners, track gap closure over the roadmap.',
    connectors_system_inventory: 'Inventory of every system reachable via a connector. Single source of truth for the data map.',
    connectors_traceability: 'End-to-end lineage of a data element across systems linked through connectors. Essential for ROPA accuracy and subject-request fulfilment.',
    consent_properties_change_requests: 'Pipeline of proposed changes to a consent property (added purpose, new language, updated policy link). Governance review before production rollout.',
    consent_properties_comparison: 'Side-by-side comparison of the consent property shape across domains or deployments. Flags drift that would weaken enforceability.',
    governance_playbooks: 'Playbook templates driving repeatable governance workflows (vendor onboarding, DPA review, transfer assessment).',
    incident_ops_coordination: 'Cross-team coordination dashboard for an active incident. One place to see who is on it, what they are doing, and what is blocked.',
    incident_ops_escalation: 'Escalation log for active incidents. Severity transitions, stakeholder notifications, and executive briefs all recorded here.',
    incident_ops_post_review: 'Post-incident review: root-cause analysis, timeline, remediation actions, and lessons learned. Feeds into training and playbook updates.',
    incident_ops_regulator_exports: 'Generate regulator submission packages for notifiable incidents. Mẫu số 08 (breach) + supporting evidence bundle.',
    incident_ops_vendor_coordination: 'Coordinate response with the vendor whose system was breached. Contract obligations, response SLAs, and shared evidence chain.',
    monitoring_authority_readiness: 'Readiness checklist and evidence pack for upcoming regulator visits. Consolidated view of outstanding gaps and completed controls.',
    monitoring_change_review: 'Periodic review of control changes across the monitoring program. Detects drift before it becomes a compliance gap.',
    monitoring_framework_link: 'Link monitoring controls to specific regulatory framework articles. Generates the evidence map regulators expect.',
    monitoring_ric: 'Regulatory Inquiry and Correspondence cases. Track questions and responses from the regulator across time.',
    forms: 'Regulatory form catalog, form filler, form instances, and filing case links. Backbone for Mẫu số 02a/02b and related submissions.',
    monitoring_tasks: 'Monitoring task queue: scheduled control verifications, attestations, and evidence refreshes.',
    partner_workspace_reports: 'Reports shared across the partner network: compliance health, monthly summaries, cross-org metrics.',
    partner_workspace_tickets: 'Support tickets raised between partners on shared workflows and escalations.',
    program_governance_audit_readiness: 'Audit-readiness scoring with drill-down into missing evidence, gap register, and remediation owners.',
    program_governance_drafting: 'Document drafting workspace with versioning, review routing, and approval workflow.',
    program_governance_evidence_gaps: 'Evidence-gap register: which controls lack current evidence and when the refresh is due.',
    program_governance_executive_summaries: 'Executive summary templates with auto-populated KPIs for board and executive briefings.',
    program_governance_health: 'Overall program health dashboard: maturity, coverage, and trend indicators.',
    program_governance_issuance: 'Document issuance workflow: approval, publication, stakeholder notification.',
    program_governance_model: 'Governance operating model: roles, responsibilities, committees, and decision rights.',
    program_governance_remediation: 'Remediation plans tied to identified gaps. Owner, deadline, status, and verification evidence.',
    program_governance_review_cadence: 'Scheduled review cadence for governance artefacts: policies, procedures, controls, evidence.',
    program_governance_trends: 'Trend dashboard: maturity over time, evidence freshness, gap closure velocity.',
    task_management_conflicts: 'Detect conflicting task assignments (double-booked owners, overlapping deadlines) and resolve via reassignment.',
    task_management_consolidation: 'Consolidate tasks auto-generated by multiple modules into a single workflow for the assignee.',
    localization_dashboard: 'Localization module overview: framework catalog health, pack status, translation coverage, and pending regulatory updates in one dashboard.',
    localization_frameworks: 'Regulatory framework catalog: PDPL, Decree 13/2023, Decree 356/2025, Cybersecurity Law, and GDPR. Each framework links articles to platform controls.',
    localization_framework_channels: 'Regulator submission channels linked to each framework (MPS portal, MIC endpoint, authority email). Drives the routing of Mẫu số filings.',
    localization_framework_detail: 'Single-framework detail view: articles, procedures, personas, linked forms, and implementation depth across the platform.',
    localization_framework_module_links: 'Framework → module coverage map. Which regulatory articles are enforced by which platform module.',
    localization_language_packs: 'Language pack catalog across 16 locales: install status, translation completeness, and pack submission history.',
    localization_audit: 'Audit trail for localization changes: framework edits, pack updates, submission events.',
    localization_market_packs: 'Market pack catalog bundling framework + language + forms per market (VN, EU member states, etc.).',
    localization_module_mappings: 'Module mapping register: which framework article applies to which module at the feature level.',
    localization_pack_detail: 'Single pack detail view: version, frameworks included, translation state, installation history.',
    localization_pack_submissions: 'Pack submission pipeline: partner submissions, review states, publication approvals.',
    localization_regulatory_updates: 'Regulatory Updates: the register of what changed in your installed frameworks — the updates you record, and the requirement-by-requirement comparison written when a new pack version or a replacing final text is installed. Review a comparison and see which form records to re-read.',
    localization_sector_overlays: 'Overlay Catalog, under Sector Overlays › Overlay Management: install the optional sector overlays your organisation is subject to and choose Direct or Reference only for each. An overlay installed Direct brings its sector registers into use, and their sector group appears under Sector Overlays in the menu. An overlay installed Reference only adds no views; a sector group is shown only while an overlay that declares it is installed Direct.',
    localization_help_content_packs: 'Help-Content Pack manager: install, version, and translate the in-app help articles, module guides, and quick-starts that surface across the admin UI.',
    localization_translation_preview: 'Live preview of UI strings across locales. Side-by-side comparison for translation QA.',
    organizations_groups_create: 'Create a new organization group (koncern): pick root org, select members, define inheritance mode per category.',
    organizations_groups_detail: 'Single org group detail: members, inheritance matrix, shared resources, and audit summary.',
    organizations_groups_settings: 'Org group settings: SLA, calendar, notifications, intake, CMP integration. Per-category inherit/override toggle.',
    organizations_groups_audit: 'Per-group audit log: membership changes, inheritance toggles, shared-resource add/remove.',
    organizations_groups_consolidated_audit: 'Consolidated audit view across all member orgs of a group. Filter by entity, actor, time window.',
    organizations_groups_dashboard: 'Cross-member dashboard: compliance health, open items, SLA breaches rolled up for the whole group.',
    organizations_groups_reports: 'Group-level reports: executive summary, regulator-facing rollup, cross-member KPI comparison.',
    organizations_groups_users: 'Users visible across the group: cross-org role assignments, shared administrators, impersonation chain.',
    organizations_groups_merged_vendors: 'Deduplicated vendor view across member orgs — a single vendor appears once even if onboarded separately.',
    organizations_groups_shared_templates: 'Form templates published at the group level. Members opt in to inherit a template; opt-out is per-org.',
    organizations_groups_shared_frameworks: 'Regulatory frameworks shared across the group. Central update flows to every member with mode=inherit.',
    organizations_groups_shared_vendors: 'Group-level vendor registry. Contract, DPA status, and risk score visible to every member.',
    onboarding_help_center: 'Help Center: browse all 309+ articles by module, category, and persona. Full-text search across EN + VI content.',
    onboarding_progress: 'Onboarding progress tracker: which go-live steps are complete, which are pending, who owns the blockers.',
    onboarding_personas: 'Persona Paths: find your role among every role available to your organisation, starting with the roles your installed frameworks name. Open a role to see the frameworks that name it, the registers it works in, its quick start and use cases, and save it as your onboarding persona.',
    onboarding_quick_starts: 'Quick-start scenarios: run a complete compliance workflow end-to-end in the sandbox. Ideal for training.',
    onboarding_sample_data: 'Sample data generator: load realistic demo rows into any module. Useful for sales demos and training.',
    onboarding_tours: 'Interactive guided tours. Launch a module-specific walkthrough that narrates each page as you navigate.',
    security_sessions: 'Live view of authenticated sessions: device, IP, last activity, MFA factor used. Terminate with one click.',
    security_api_keys: 'Service-account API keys: scope, rotation, last-used, and per-key audit trail.',
    security_break_glass: 'Emergency break-glass access: time-bounded super-admin grant with mandatory audit trail and post-event review.',
    security_delegations: 'Lend another member some or all of your own permissions for a fixed window. They hold them from the start until the end or until you revoke, checked on every request, and each use is recorded in the security audit trail.',
    security_ip_allowlist: 'IP allowlist configuration: restrict admin login to specific corporate networks or VPN ranges.',
    security_mfa: 'Multi-factor authentication policy: enforcement per role, factor allowlist (TOTP/SMS/WebAuthn), reset flow.',
    security_service_accounts: 'Service accounts for system-to-system integration. Scoped, audited, and rotated separately from human users.',
    security_session_settings: 'Session policy: idle timeout, max session age, concurrent-session limit, re-authentication triggers.',
    security_sso: 'Single sign-on configuration: SAML / OIDC providers, attribute mapping, auto-provisioning rules.',
    rights_requests_create: 'Create a new data-subject rights request case. Captures requester identity, request type, channel, and SLA clock start.',
    rights_requests_list: 'All rights-request cases with SLA countdown, assignee, status, and filter chips for channel + request type.',
    rights_requests_detail: 'Single case workspace: correspondence, fulfillment actions, evidence, response drafting, SLA transitions.',
    rights_requests_cmp_sync: 'CMP integration sync settings: webhook endpoint, auto-case creation rules, retry policy.',
    rights_requests_extensions: 'SLA extension requests: justification, approver routing, and audit-trail entry at grant/decline.',
    rights_requests_fulfillment: 'Request fulfillment workspace: gather data packages, apply redactions, package for delivery.',
    rights_requests_responses: 'Response drafting with template insertion, merge fields, and side-by-side EN/VI preview.',
    rights_requests_response_templates: 'Response template catalog: approved templates per request type + jurisdiction, with version history.',
    compliance_forms_consolidation_create: 'Create a new consolidation case: group related form submissions under a single filing package destined for one regulator.',
    compliance_forms_consolidation_detail: 'Single consolidation case workspace: section inputs, reviewer approvals, export of the complete dossier to the regulator.',
    compliance_forms_consolidation_cases: 'All consolidation cases with filing status, reviewer assignments, and deadline tracking.',
    compliance_forms_consolidation_review: 'Review workspace for consolidation cases: approve section inputs, request revisions, sign off before export.',
    compliance_forms_dossier_export: 'Export the consolidated dossier as a regulator-ready PDF bundle with cover letter, annexes, and signatures.',
    compliance_forms_section_input: 'Department owner workspace for completing a single section of a consolidation case.',
    compliance_forms_filing_case_links: 'Links between form instances and filing cases. Ensures every filing is backed by the right form data.',
    compliance_forms_section_routing: 'Section routing rules: which department owns which section of each form template.',
    assessments_dashboard: 'Assessments dashboard: DPIA count, transfer assessments, gap summary, upcoming review deadlines.',
    assessments_dossier_filing_detail: 'Single dossier filing: form type (Mẫu số 02a/02b), section assignments, section status, submission details, MPS timers.',
    assessments_dossier_filing_list: 'All dossier filings with form type, status, and section-assignment progress. Filter by status and framework.',
    assessments_dpia_list: 'DPIA register: all in-progress, approved, and archived Data Protection Impact Assessments.',
    assessments_gap_report: 'Gap Report: DPIA coverage, unclassified DPIA requirement, unmitigated risks, missing filings. Primary audit-readiness surface.',
    assessments_transfer_list: 'Transfer assessments for cross-border data flows. Each assessment documents mechanism, safeguards, and risk rating.',
    deletion_orchestration_templates: 'Checklist templates for deletion runs. Each template is a scripted sequence of tasks tied to specific systems and retention rules.',
    deletion_orchestration_dsr_integration: 'DSR-to-deletion integration: auto-trigger a deletion run from an approved erasure request.',
    deletion_orchestration_tasks: 'Execution tasks for active deletion runs. Each task tracks system, action, owner, and verification evidence.',
    deletion_orchestration_dashboard: 'Deletion orchestration dashboard: active runs, completion rate, pending verifications, proof-chain freshness.',
    deletion_orchestration_proof_chain: 'Tamper-evident proof chain for every deletion action. Required for PDPL compliance on erasure.',
    organizations_compliance_rollup: 'Compliance rollup across entities: KPI by entity, framework-level coverage, outstanding obligations.',
    organizations_departments_admin: 'Admin workspace for departments: create, reassign owners, manage team membership, set routing rules.',
    organizations_departments: 'Departments list across entities. Drives section-routing, approval flows, and evidence attribution.',
    organizations_entity_frameworks: 'Assign regulatory frameworks to each entity. Drives which Mẫu số forms apply and which audits are mandatory.',
    // fix-sprint #447 W8 — the form's own fields; it records no business registration, address or representative.
    organizations_entity_form: 'Create or edit one organization: its name and slug, timezone, default locale, country and parent organization. Country stays not recorded until someone chooses one. When you edit an organization, Deactivate is here too.',
    organizations_list: 'All organization entities across the tenant. Primary list view with status filters and quick-access actions.',
    organizations_retention_policies_admin: 'Retention policy administration scoped to the organization: default rules, per-entity overrides.',
    task_management_calendar: 'Calendar view of tasks and deadlines. Color-coded by module and SLA state.',
    task_management_my_tasks: 'Personal task queue: assigned to me, due soon, overdue. Primary daily workspace.',
    task_management_overdue: 'Overdue tasks across the organization. Filters by owner, module, and days-overdue bucket.',
    task_management_section_assignments: 'Section-assignment overview: which department owns which section of each active form filing.',
    task_management_board: 'Kanban board view of tasks. Drag between columns (To-Do / In-Progress / Review / Done) to update status.',
    task_management_detail: 'Single task workspace: description, comments, attachments, sub-tasks, audit trail, status transitions.',
    task_management_list: 'Flat list of all tasks with filters: owner, module, status, priority, due-date range.',
    consent_cmp_sync: 'CMP sync workspace: trigger syncs, review sync history, inspect failures. Keeps CMP state aligned with the platform.',
    consent_cmp_bulk_sync: 'Bulk CMP sync: re-synchronise all consent records across all properties. Used for tenant migrations and large CMP changes.',
    consent_cmp_integration: 'CMP integration settings: endpoint, authentication, sync policy, and connection health.',
    consent_cmp_purpose_mapping: 'Map CMP purposes to platform purposes. Ensures consent signals flow correctly between the two systems.',
    consent_cmp_webhook: 'CMP webhook config: inbound event routing, signing secrets, retry policy.',
    partner_workspace_brand_profiles: 'Partner brand profiles: logo, tagline, contact, service offerings. Drives the partner marketplace listing.',
    partner_workspace_deliverable_templates: 'Deliverable template catalog: standardised service packages partners can offer (DPIA review, vendor audit, training).',
    partner_workspace_deliverables: 'Active deliverables across partner engagements. Track scope, due dates, acceptance criteria.',
    partner_workspace_service_tiers: 'Service tier definitions: Bronze / Silver / Gold with scope, SLA, and pricing bands.',
    partner_workspace_workflow_instances: 'Active workflow instances across partners: onboarding, review, escalation, offboarding runs.',
    partner_workspace_workflow_templates: 'Workflow template catalog for partner engagements. Each template defines steps, owners, SLAs, and outputs.',
    access_accountability_justification_reviews: 'Review access justifications submitted by users. Approve, challenge, or escalate per zone policy.',
    access_accountability_review_campaigns: 'Scheduled access-review campaigns: recurrence, scope, reviewer assignment, completion tracking.',
    access_reviews_list: 'All access-review campaigns with filters: cycle, status, reviewer, target system.',
    access_reviews_campaign_detail: 'Single campaign workspace: scope, reviewers, item queue, certification state, audit trail.',
    access_reviews_my_reviews: 'Reviews assigned to me: pending decisions with context, certify or revoke per item.',
    attestation_schedules_list: 'Attestation schedule registry: recurrence, scope, next-due date, completion rate per schedule.',
    attestation_schedules_create: 'Create a new attestation schedule: pick frequency, scope, attesters, and evidence requirements.',
    attestation_schedules_edit: 'Edit an existing attestation schedule. Changes apply to the next cycle; past cycles remain unchanged.',
    attestation_schedules_history: 'Historical completion timeline of an attestation schedule. Evidence artefacts linked per cycle.',
    governance_board_report: 'Board-ready compliance report: executive summary, score trends, outstanding risks, remediation status.',
    governance_compliance_score: 'Compliance score computation across frameworks. Drill-down into per-control contribution.',
    governance_drift_monitor: 'Detect control drift: settings changes, permission grants, policy exceptions that weaken posture.',
    governance_audit: 'Governance-specific audit log: workflow events, approval decisions, drift signals.',
    governance_evidence: 'Evidence catalog for governance controls. Supporting documents, screenshots, attestation receipts.',
    partner_workspace_escalation: 'Cross-partner escalation dashboard: open escalations, severity, SLA clocks, and next-action owners.',
    partner_workspace_audit: 'Audit log for partner workspace: engagement changes, deliverable updates, escalation transitions.',
    partner_workspace_partner_detail: 'Single partner organisation profile: engagement history, active deliverables, contacts, performance score.',
    partner_workspace_partner_list: 'Partner organisation registry: active, onboarding, and offboarded partners with service tier + performance badges.',
    platform_license: 'License settings: tier, seat counts, renewal status, feature toggles driven by the installed license.',
    platform_notifications_admin_queue: 'Admin notification queue: in-flight alerts, delivery failures, retry state across email/SMS/in-app channels.',
    platform_notifications_prefs: 'Per-user notification preferences: channel opt-in, digest schedule, muted topics.',
    settings_overview: 'Tenant settings overview: quick links to domains, email, exports, webhooks, and other platform-wide config.',
    settings_domains: 'Allowed domains for sign-up and SSO. Restricts account creation to verified corporate domains.',
    settings_email: 'Outbound email configuration: SMTP provider, sender identity, DKIM/SPF status, test-email tool.',
    settings_export_schedules: 'Scheduled exports: recurring CSV/XLSX/PDF generation delivered to File Browser on a cron schedule.',
    settings_webhooks: 'Outbound webhook endpoints: subscribe external systems to platform events. Signing, retry, audit of every delivery.',
    sod_rules_create: 'Create a Segregation-of-Duties rule: pick conflicting role pairs, scope, and enforcement mode (block/warn).',
    sod_rules_edit: 'Edit an existing SoD rule. Changes take effect immediately; existing violations are re-evaluated on next access.',
    sod_rules_list: 'All SoD rules across the tenant: enforcement mode, role pairs, exception count, violation count.',
    connectors_definitions: 'Connector type registry: definition of each integration (endpoint shape, auth, supported operations).',
    connectors_list: 'All connector instances wired to the tenant. Status, last sync, error log, sync-on-demand action.',
    connectors_credentials: 'Connector credentials vault: OAuth tokens, API keys, service-account passwords. Rotation schedule + audit.',
    connectors_event_bus: 'Connector event bus: inbound events from integrations, routing rules, replay dead-letter queue.',
    consent_change_awareness: 'Detect meaningful consent changes: purpose added, policy updated, scope widened. Drives re-consent campaigns.',
    consent_audit: 'Consent audit trail: every grant, update, withdrawal across workflows. Primary surface for regulator walkthroughs.',
    consent_reconciliation: 'Reconcile consent state across CMP, platform, and downstream systems. Flags discrepancies.',
    consent_withdrawal_propagation: 'Withdrawal propagation pipeline: when a subject withdraws consent, ensure it flows to every linked system.',
    consent_policies: 'Consent policy catalog: privacy policy versions, language variants, activation windows.',
    consent_purpose_catalog: 'Purpose catalog used by consent workflows. Each purpose links to a legal basis and a retention rule.',
    consent_snapshots: 'Consent snapshots: point-in-time capture of all active consents. Required for audits and legal holds.',
    consent_workflows: 'Consent workflow catalog. Each workflow defines triggers, collection flows, and downstream actions.',
    discovery_classification_lineage: 'Classification lineage: trace how a data element was classified across scans and manual review.',
    discovery_classification_rules: 'Classification rules registry: regex, vocabulary, ML patterns used to auto-classify discovered fields.',
    discovery_dedup_rules: 'Deduplication rules for discovered records. Prevents the same field from appearing multiple times.',
    discovery_import_templates: 'Import templates for bringing existing data catalogs into the Discovery module via CSV or external tools.',
    discovery_owner_review: 'Owner-review queue: suggested classifications awaiting data-owner approval or rejection.',
    discovery_staging: 'Staging area for newly discovered fields. Triage before promotion to the Data Mapping catalog.',
    discovery_suggestion_analytics: 'Analytics for the suggestion engine: accuracy, coverage, most-overridden classifications.',
    discovery_suggestion_queue: 'Review queue of suggested classifications before they are applied. Accept, modify, or reject per item.',
    compliance_forms_filler: 'Interactive form filler: step through a regulatory form with validation, autosave, and co-author support.',
    compliance_forms_instance_detail: 'Single form instance: current state, history, linked filing case, exports.',
    compliance_forms_outputs: 'Generated outputs per form instance: PDF, DOCX, JSON packages with cover letter and signatures.',
    compliance_forms_submission_packages: 'Submission package catalog: regulator-ready bundles with manifest, signatures, and delivery receipt.',
    incident_ops_regulatory_sla: 'Regulatory SLA configuration for incident notifications (VN Cybersecurity Law 72-hour window, PDPL 72-hour, GDPR 72-hour).',
    incident_ops_response_metrics: 'Incident response metrics: MTTD, MTTR, SLA compliance rate, escalation counts.',
    incident_ops_sla_dashboard: 'SLA dashboard: active incidents against their SLA clock, at-risk warnings, breach history.',
    incident_ops_sla_definitions: 'SLA definitions: severity tiers, per-tier response and resolution windows, framework citations.',
    monitoring_governance_raci: 'RACI matrix for governance controls: responsible, accountable, consulted, informed per control across the program.',
    monitoring_periods: 'Monitoring periods: annual/quarterly/monthly review cycles aligned to regulatory filing windows.',
    monitoring_programs_list: 'Monitoring program catalog: framework-aligned programs with scope, owner, cadence.',
    monitoring_task_templates: 'Task templates for monitoring cycles. Each template defines scope, assignee role, evidence requirements.',
    access_accountability_evidence_detail: 'Single evidence package detail: chain of custody, signatures, export options for regulator submission.',
    access_accountability_evidence_packages: 'Evidence package catalog for access-accountability: signed, tamper-evident bundles ready for audit.',
    access_reviews_role_analytics: 'Role analytics: role-usage density, entitlement creep, certification-decision trends.',
    access_accountability_pattern_analysis: 'Pattern analysis over access logs: unusual access times, geographical outliers, privilege escalation signals.',
    access_accountability_session_dashboard: 'Session dashboard: live authenticated sessions, session heatmap by hour/day, inactive-session alerts.',
    classification_exports: 'Classification exports: export the classification inventory as CSV/XLSX for external audit tools.',
    classification_discovery_cycles: 'Classification discovery cycles: scheduled scans that surface newly-added or changed data elements.',
    classification_enterprise_data_map: 'Enterprise data map: aggregated classification view across systems, departments, and sensitivity tiers.',
    connectors_automation_planning: 'Automation planning: define trigger → connector → action chains for event-driven data operations.',
    connectors_brd_capture: 'Business Requirements Document capture for new connector build-outs.',
    connectors_poc_scorecards: 'Proof-of-concept scorecards for evaluating new connector integrations against acceptance criteria.',
    discovery_connector_management: 'Discovery connector management: which scanners are installed, their health, and which systems they target.',
    discovery_scans_list: 'All discovery scans with status, coverage, and discoveries. Drill-down into per-scan findings.',
    governance_approvals: 'Governance approvals register: requests recorded for vendor, cross-border, playbook and drift decisions, with who decided each and why. A request is decided by someone other than the person who made it.',
    governance_offboarding: 'Vendor offboarding workflow: contract termination, data return/deletion, access revocation, final audit.',
    governance_periodic_review: 'Periodic vendor review: scheduled reassessment of risk, DPA currency, subprocessor list.',
    incident_ops_playbook_activations: 'Active playbook instances: which playbook is running on which incident, progress, outstanding tasks.',
    incident_ops_playbook_links: 'Playbook → incident linkage registry. Also links to regulator notification templates per playbook.',
    incident_ops_playbook_templates: 'Playbook template catalog: reusable incident-response scripts per incident type and severity.',
    organizations_groups_settings_inheritance: 'Settings inheritance matrix across group members. Per-category inherit/override toggle with audit trail.',
    organizations_intake_config_form: 'Create or edit an intake config: public-form URL path, rate limits, captcha, notification routing.',
    organizations_intake_configs_list: 'Intake config catalog: public-facing rights-request entry points per market/brand.',
    privacy_scanner_monitoring_dashboard: 'Privacy-scanner monitoring dashboard: property coverage, scan cadence, signals flagged per property.',
    privacy_scanner_monitoring_settings: 'Monitoring settings: scan frequency, threshold rules, alert routing, retention of scan results.',
    privacy_scanner_settings: 'Scanner settings: enabled scan categories, signature updates, AesirX scanner endpoint credentials.',
    privacy_scanner_real_time_scan: 'Real-time scan: trigger an ad-hoc scan and watch results stream in. Ideal for rapid verification.',
    privacy_scanner_scan_result_detail: 'Detail view of a single scan result: cookies, beacons, trackers found, classification, remediation actions.',
    privacy_scanner_results_archive: 'Archive of historical scan results. Filter by property, date range, signal type.',
    task_management_workflow_detail: 'Single workflow instance: all steps, owners, SLAs, branching logic, completion evidence.',
    task_management_workflow_list: 'All workflow instances across modules: status, owner, deadline, origin module.',
    task_management_workflow_templates: 'Workflow template catalog: reusable multi-step workflows that can be instantiated from any source module.',
    audit_trail_classic: 'Classic audit trail view (legacy). Being deprecated in favour of the unified audit module.',
    audit_trail_unified: 'Unified audit trail across every module. Primary audit surface.',
    iat_settings: 'Configure the Immutable Audit Trails add-on: enablement, scope, default proof mode, taxonomy overrides, caps, and AesirX credential rotation. Read the canonical module guide first; the deep companion is "Enabling and Configuring".',
    iat_status: 'Browse every proof_link with status, mode, module, and date filters. Click any badge to open the proof drawer. Deep companion: "Understanding Proof Status".',
    iat_integrity_center: 'Run drift-detection verification passes over a date range; export the Integrity Pack for any audit scope. Deep companion: "Using the Integrity Center".',
    iat_usage: 'Track current balance, monthly consumption, cap state, and redeem credit packages. Deep companion: "Credits and Caps".',
    classification_exceptions: 'Classification exception queue: records flagged as edge cases, awaiting manual review.',
    classification_review_tasks: 'Classification review task queue: auto-suggested classifications awaiting owner confirmation.',
    consent_properties_audit_log: 'Audit trail for consent property changes: who updated what, when, old/new values.',
    consent_properties_analytics: 'Consent property analytics: consent capture rate, withdrawal rate, purpose mix trends.',
    consent_properties_evidence_packs: 'Evidence packs for consent properties: regulator-ready bundles proving consent capture compliance.',
    consent_properties_inconsistencies: 'Consent property inconsistencies: properties where the platform and CMP disagree on state.',
    consent_properties_golden_templates: 'Golden consent-banner templates: approved, reviewed, governance-ready banner shapes deployed across properties.',
    consent_properties_template_rollouts: 'Template rollout pipeline: stage, pilot, production waves for pushing golden templates to properties.',
    consent_properties_property_groups: 'Property groups: cluster related properties for governance and reporting rollups.',
    consent_properties_snapshot_schedules: 'Snapshot schedules: automate periodic consent-state captures for audit and legal-hold purposes.',
    deletion_orchestration_completeness: 'Completeness tracker: verifies every system in the retention scope confirmed deletion.',
    deletion_orchestration_runs_list: 'All deletion runs: status, duration, systems covered, completion rate.',
    deletion_orchestration_vendor_requests: 'Outbound deletion requests to vendors: tracking, SLA, verification artefacts.',
    // fix-sprint #431 — the page verifies nothing in a vendor's systems: it
    // records a follow-up per vendor deletion request and whether the vendor
    // confirmed, or that you escalated.
    deletion_orchestration_vendor_verification: 'Vendor verification: follow up each vendor deletion request with a confirmation deadline and an escalation contact, then mark it confirmed or escalated. The platform sends nothing to the vendor.',
    compliance_forms_catalog: 'Compliance form catalog: every regulatory form (Mẫu số, GDPR Art 30, ISO 27701) available for filing.',
    compliance_forms_template_browser: 'Template browser: discover approved form templates across frameworks.',
    governance_cross_border: 'Cross-border transfer registry at governance level: mechanisms, safeguards, country risk assessment.',
    governance_subprocessors: 'Subprocessor registry: who our vendors use, DPA cascade, notification of changes.',
    incidents_detail: 'Single incident workspace: timeline, owner, severity, remediation, regulator-notification status.',
    incidents_list: 'Incident register: all active + historical incidents with filters for severity, channel, framework.',
    partner_workspace_partner_dashboard: 
    // Sprint 64.169.0 — "health trend" removed: there is no health trend. The
    // health-score card has rendered a hardcoded 0 since the module shipped and
    // now honestly reads `--`; a tip promising the number while the card shows
    // `--` would make the page contradict its own help text. 64.169.1 computes
    // a point-in-time score, not a series, so the word stays out either way.
    'Partner dashboard: top-level KPIs for all partner engagements and escalation counts.',
    partner_workspace_support_tickets: 'Partner support ticket catalog. Shared between partner and tenant teams with SLA tracking.',
    program_governance_acknowledgment_tracking: 'Document acknowledgment tracking: who has read and acknowledged each policy or procedure.',
    program_governance_change_log: 'Document change log: every edit to a governance document with diff and approval chain.',
    roles_detail: 'Single role detail: permissions bound to this role, users assigned, audit of membership changes.',
    roles_list: 'Role catalog: every built-in and custom role with member count and permission scope.',
    consent_properties_cross_property_dashboard: 'Cross-property dashboard: consent health across every property. Aggregates capture rate, withdrawal rate, drift indicators.',
    consent_properties_property_registry: 'Property registry: every property the tenant manages with its consent banner state, linked CMP, policy version.',
    // Sprint 47.7.4 AC-6 — Forseti module-tip summaries (EN+VI; 14-locale fan-out → 50.10.0).
    forseti_projects_overview: 'Forseti workspaces: a project groups documents, conversations, workflow runs, and tabular reviews into one container so the AI advisor keeps context across the whole engagement.',
    forseti_workflows_list: "Saved AI prompt templates that produce tasks and draft evidence on demand. Use built-in pack workflows or author your own; each run is sent to your organisation's configured AI provider, and what it consumed is shown on Forseti → Usage.",
    forseti_tabular_reviews_list: 'Repeatable, column-by-column review of a document corpus. Define the questions once, run the same review across many attachments, and get a side-by-side answer grid with citations.',
    forseti_document_review_index: 'Triage inbox for documents with AI-suggested edits or Forseti-sourced versions. Documents enter the queue from chats, workflow runs, or the "Send a document for review" CTA on this page.',
    forseti_audit_lineage: 'Trace every AI-generated artefact attached to any record: chats, tool calls, tasks, evidence drafts, and document versions. Required for evidence defensibility and post-action audits.',
    forseti_draft_evidence: 'AI-drafted evidence pending human review. Every draft must be approved by an authorized reviewer before it becomes formal evidence — Forseti never auto-files.',
    forseti_usage: 'What Forseti has consumed for this organisation: requests and tokens for a chosen period, broken down by provider, model, effort and feature, with the routing each feature uses now and recent activity.',
    forseti_ai_model: 'Configure the AI provider behind Forseti. Each provider card holds its own API key, model, fast model and effort, with a connection test; the general section chooses which provider is active.',
    // Sprint 47.7.4.2 AC-8 — Forseti chat surface module-tip summaries.
    forseti_overview: 'Entry point to the Forseti AI Legal Advisor: pick a persona, browse recent chats, and start a new conversation. Persona selection here sets the default voice for new chats.',
    forseti_chat_detail: 'Single Forseti conversation: view the message history, switch personas mid-chat, send new messages, and export the transcript as PDF for evidence purposes.',
    forseti_cross_framework_conflicts: 'Curated catalog of conflicts between the regulatory packs your organisation has installed. Filter by framework / topic / severity / acknowledgement status, click a row to see side-by-side framework positions and acknowledge / assign / resolve. Forseti surfaces the conflicts; humans decide.',
    users_detail: 'Single user profile: identity, role, MFA, session history, audit trail.',
    users_list: 'User directory: all active, invited, and deactivated users with quick-filters and bulk actions.',
    governance_vendors: 'Manage vendor governance lifecycle: onboarding pipeline, periodic reviews, approvals, and offboarding. Track vendor compliance status and playbook adherence.',
    governance_compliance: 'Monitor organizational compliance posture with scoring, drift detection, and board reporting. Track compliance trends and generate executive-ready reports.',
    governance_transfers: 'Manage cross-border data transfers, DPA reviews, and sub-processor governance. Document transfer mechanisms and ensure regulatory compliance for international data flows.',
    vendor_risk_assessment: 'Assess and score vendor risk across your portfolio. Monitor risk signals, track reassessments, and maintain a comprehensive risk portfolio view.',
    vendor_risk_contracts: 'Manage vendor contracts, due diligence questionnaires, and gate evaluations. Track contract terms, renewal dates, and compliance requirements.',
    vendor_risk_onboarding: 'Manage the vendor onboarding lifecycle: intake assessment, review routing, approval workflows, and risk acceptance decisions.',
    vendor_risk_dashboard: 'Central overview of your vendor risk posture: portfolio distribution by tier, recent tier changes, contracts expiring within 90 days, review KPIs, and risk-acceptance health. Start here for an at-a-glance read before drilling into scoring, signals, or gates.',
    vendor_risk_scoring: 'Configure scoring dimensions and evaluate vendors against weighted risk criteria. Track composite scores, assigned tiers, and score history over time.',
    vendor_risk_signals: 'Track adverse events that may warrant vendor reassessment: certification expiry, breach notifications, financial instability, and compliance changes. Signals drive downstream reassessment tasks.',
    vendor_risk_reviews: 'Conduct periodic vendor reviews with contract-status snapshots, findings, and recommendations. Track review outcomes (satisfactory, concerns identified, remediation required, escalated) across programs and periods.',
    vendor_risk_reassessments: 'Trigger and manage reassessment tasks when signals or periodic schedules indicate changed risk posture. Record outcomes and updated risk levels; completed reassessments flow back to scoring.',
    vendor_risk_acceptance: 'Formally accept residual vendor risk with explicit conditions, expiry dates, and justification. Maintain the Risk Acceptance Register — the auditable record of active, expired, and revoked acceptance decisions.',
    vendor_risk_onboarding_cases: 'Manage the vendor onboarding case lifecycle: new onboarding, material-change reviews, and periodic reviews. Work through staged tabs with completion indicators and trigger approval gates when ready.',
    vendor_risk_review_routing: 'Route vendor review tasks to specific reviewers by user and role. Assign questionnaires, track reviewer assignments, and capture routing outcomes with full audit trail.',
    vendor_risk_gates: 'Evaluate procurement approval gates before vendors go live. Configure gate criteria (certifications, DPA signed, questionnaire complete, risk within threshold), record override justifications, and link evidence.',
    vendor_risk_questionnaires: 'Build due-diligence questionnaire templates and assign them to vendors. Track response completion, review annotations, and findings that feed into risk scoring and approval gates.',
    monitoring_programs: 'Create and manage compliance monitoring programs linked to regulatory frameworks. Assign tasks via RACI governance and track program execution.',
    monitoring_kpi: 'Track compliance KPIs, authority readiness scores, and change review status. Dashboard view of monitoring program performance across all active frameworks.',
    discovery_scans: 'Run data discovery scans across connected systems. Manage discovery cycles with delta detection, configure connectors, and track scan results.',
    discovery_classification: 'Review discovery suggestions, manage the staging area for newly discovered data, and conduct owner review with classification recommendations.',
    discovery_management: 'Configure deduplication rules, manage import templates, and trace classification lineage from discovery through to approved classification labels.',
    connectors_integration: 'Configure system connectors, manage credentials, and monitor the event bus for real-time data synchronization across integrated systems.',
    connectors_automation: 'Plan automation strategies, capture business requirements, and evaluate proof-of-concept scorecards for system integration projects.',
    connectors_governance: 'Manage the system inventory, conduct security reviews, maintain traceability matrices, and track integration gap registers.',
    task_management_tasks: 'View and manage compliance tasks: task list, personal task queue, overdue tasks, task board, and calendar views for tracking work across all modules.',
    task_management_workflows: 'Design and manage compliance workflows: create workflow templates, configure section assignments, and monitor workflow execution.',
    compliance_forms_templates: 'Browse and manage compliance form templates. Access the template catalog, create custom templates, and configure form layouts for regulatory submissions.',
    compliance_forms_submissions: 'Complete and submit compliance forms. Track form instances, review outputs, and manage submission packages for regulatory filing.',
    compliance_forms_filing: 'Manage filing case links, section routing, and multi-department consolidation for regulatory dossier preparation and submission.',
    organizations_entities: 'Manage organization entities, assign regulatory frameworks per entity, and configure department hierarchies for multi-entity compliance.',
    organizations_groups: 'Manage organization groups for multi-entity enterprises. Share vendors, frameworks, and form templates across group members with consolidated reporting.',
    organizations_intake: 'Configure intake settings and manage settings inheritance across organization hierarchies.',
    access_accountability_sessions: 'Monitor user sessions, review access logs, and analyze access patterns. Track session activity with anchoring status for accountability evidence.',
    access_accountability_reviews: 'Run access review campaigns, manage justification reviews, and track reviewer progress. Coordinate periodic access certification across the organization.',
    access_accountability_sod: 'Define separation of duties rules, detect violations, and manage exceptions. Enforce conflict-of-interest controls across roles and permissions.',
    access_accountability_evidence: 'Generate evidence packages for access reviews, conduct investigations, and analyze role usage patterns for access accountability reporting.',
    program_governance_maturity: 'Assess and track compliance program maturity across frameworks. Define maturity models, run assessments, and monitor improvement trends over time.',
    program_governance_obligations: 'Map regulatory obligations to organizational units with RACI governance. Define the governance model and track remediation plans for identified gaps.',
    program_governance_documents: 'Manage the governance document register: draft, review, issue, and track acknowledgment of compliance policies and operating documents.',
    program_governance_reporting: 'Generate executive summaries, audit readiness reports, and evidence gap analyses. Track reporting periods and monitor program health metrics.',
    deletion_orchestration_runs: 'Execute and monitor deletion orchestration runs. Track execution tasks, verify completeness, and manage the deletion dashboard.',
    deletion_orchestration_vendors: 'Coordinate vendor deletion requests, verify vendor compliance with deletion obligations, and maintain proof chain documentation.',
    consent_management: 'Manage consent policies, purpose catalogs, and consent workflows. Configure consent collection rules and track consent snapshots over time.',
    consent_cmp: 'Integrate with Consent Management Platforms (CMPs): configure webhooks, map CMP purposes to GRC catalog purposes, and synchronize consent records.',
    consent_compliance: 'Monitor consent compliance: detect configuration changes, reconcile mismatches, track withdrawal propagation, and review the consent audit trail.',
    partner_workspace_partners: 'Manage partner organizations, conduct partner audits, and handle escalations. Track partner onboarding status and compliance requirements.',
    partner_workspace_operations: 'Manage partner deliverables, workflow templates, service tiers, and brand profiles. Configure operational workflows for partner collaboration.',
    privacy_scanner_scanning: 'Run real-time privacy scans on websites and applications. Review scan results, track pre-consent tracking evidence, and manage the scan archive.',
    privacy_scanner_monitoring: 'Configure privacy scanner monitoring dashboards and settings. Set up automated scanning schedules and manage scanner configuration.',
    consent_import: 'Bulk import consent records from CSV files. Ideal for registering paper-based consent (employment agreements, parent/school consent forms) at scale. Supports grant, withdrawal, restriction, and objection workflow types.',
    cases_import: 'Bulk import data subject rights request cases from CSV files. Supports multi-channel intake sources including phone, postal, email, and in-person submissions for organizations receiving high volumes of DSARs.',
    data_subjects: 'View unified data subject profiles aggregating all rights request cases and consent records for each individual. Track the complete privacy lifecycle per person — consent history, DSR cases, and interaction timeline.',
    files_config: 'Set the platform-wide file upload policy — size limits, type allow-list, retention defaults, and legal-hold rules. Per-owner-type overrides let you tighten policy for sensitive modules (e.g. rights request evidence) while keeping the rest of the org on a default. Retention defaults support the data-minimisation expectations under PDPL Law 91/2025 Article 3; legal hold suspends deletion during open investigations or disputes.',
    files_browser: 'Browse, search, and manage every attachment uploaded across the organization. Filter by owner module, uploader, date, and MIME type; open the attachment detail drawer to see audit history, linked entity, and retention status. An operational view — not framework-specific — used by admins and auditors to find and triage files.',
    files_dashboard: 'Operational overview of the file surface: total storage, growth over time, orphaned attachments (no parent entity), and retention policy compliance by owner type. Use it to spot policy drift (e.g. a module retaining files past the global retention window) and to budget storage. Operational tooling — not tied to a specific regulatory framework.',
    // Module Guide records for pages that rendered no guide (idea-catalog #79).
    continuity: 'Continuity plans and the exercises that test them. Record each exercise, the improvement actions it raised and whether each action has been retested — an exercise with actions still awaiting a retest is not shown as finished. The rhythm shown is the one your organisation sets.',
    deidentification: "De-identification runs over personal data. Each run records its method, a sample check and a named person's judgement of the residual re-identification risk. A run is approved only once both are recorded, and the register shows what a draft is still missing.",
    rights_requests_batch: 'Upload a CSV of rights requests and let Forseti draft a response for each row. Every draft waits for a person to accept or reject it — nothing is sent on the platform’s own initiative.',
    assessments_dossier_materialisation_health: 'The health of dossier creation for approved filings: filings still waiting for their dossier, filings whose last attempt failed and can be retried, and orphan dossiers that no filing references. Detection only — nothing is cleaned up automatically.',
    pdpl_evidence_pack_builder: 'Assemble an impact-assessment evidence pack from its part files. The pack is built the same way every time and shows its checksum and manifest, so the pack you send is the pack you reviewed.',
    pdpl_evidence_pack_detail: 'One evidence pack: its manifest, status and linked dossier, with the draft cover email, the send action and the history of every send. A pack is not edited here — rebuild it if something is wrong.',
    pdpl_evidence_pack_draft: 'Compose and edit the cover email for an evidence pack, one draft per language. Read the channel notice shown above the body before you edit it; the draft is yours to review and send.',
    pdpl_evidence_pack_email_providers: 'Configure the email provider your organisation sends evidence packs through. Packs are sent from a provider you configure — the platform is never the default sender.',
    forseti_project_detail: 'One Forseti project: its folders and documents, its members, and the conversations, reviews and drafts gathered in it so the advisor keeps its context. Rename or delete the project from its overview.',
    forseti_mcp: 'Connect Claude, Slack agents and other MCP clients to this organisation. Copy the connection details, register OAuth clients, review the apps people have connected, and issue API keys for scripts. A client secret or API key is shown once only — copy it before closing the dialog.',
    forseti_document_review: "Review a document's AI-suggested edits version by version, and accept or reject each one. Nothing Forseti suggests changes the document until a person accepts it.",
    forseti_tabular_reviews_compare: 'Compare two tabular reviews side by side over the documents they share. Matching questions are lined up, answers that differ are marked, and questions only one review asks are listed separately. Read-only.',
    forseti_tabular_reviews_detail: 'One tabular review: a grid of documents against your questions, with each answer cited to its source. The estimated cost is shown before a run starts, and cells fill in as they complete.',
    forseti_memory: 'Browse and curate what Forseti has learned about your organisation across conversations. Edit or delete an entry, or promote it to curated so the nightly retention sweep keeps it and every persona can use it.',
    credit_information_uses: "Record each use of a data subject's credit information, the purpose it served and the consent it rested on. The purposes come from your installed frameworks, and a use that needs consent is refused while none is recorded. The platform computes no score and makes no lending decision.",
    legal_matters: 'Legal matters your organisation has received — complaints, denunciations, claims and others. Each shows whether it rests on a right an installed framework declares or is tracked by choice, and a matter not yet acknowledged to whoever raised it is never shown as settled.',
    onboarding_persona_detail: 'One persona: the role it describes, its market, its suggested first task and its primary modules — marked against the modules enabled for your organisation where that can be read — with links to its use cases. The content is read from the product’s persona registry, not written for this page.',
    onboarding_use_case_detail: 'One use case from the reference set and the persona it is written for, with a link to that persona and to its onboarding path. Its steps are in its reference document, which this page names rather than restates. The content is read from the product’s use-case registry, not written for this page.',
    procedure_continuity_exercise_retest: 'Continuity Exercise and Retest',
    procedure_deidentification_run: 'De-identification Run Approval',
    procedure_ai_output_human_review: 'Human Review of AI-Assisted Output',
    procedure_evidence_pack_submission: 'Evidence Pack Assembly and Submission',
    procedure_mcp_key_lifecycle: 'Connecting MCP clients: OAuth clients, connected apps and API keys',
    procedure_framework_conflict_resolution: 'Cross-Framework Conflict Resolution',
    procedure_credit_information_use: 'Recording a Credit-Information Use',
    procedure_legal_matter_intake: 'Legal Matter Intake and Acknowledgement',
};
export const enDeadlineActivation = {
    token_live: 'In force',
    token_upcoming: 'Not yet in force',
    in_force_from: 'In force from {date}',
    start_date_not_stated: 'Start date not stated',
    token_unknown: 'Timing unknown',
    unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
    retry: 'Retry',
    retry_busy: 'Retrying…',
    // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
    // Sprint 63.31.0 (#423) — the record was read and names a phase it does not date.
    token_phase_unresolved: 'Phase start not dated',
    phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
    token_not_stated: 'No effective date stated',
    not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
    retry_subject: 'the regulatory deadlines',
    column_heading: 'When it applies',
    widget_overlays_failed: 'The installed sector overlays could not be read.',
    widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
    widget_retry_subject: 'the sector overlay deadlines',
    settings_sources_label: 'Deadlines behind this suggestion',
    settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
    settings_unknown_not_counted: 'Not used for the suggestion.',
    // Sprint 63.33.0 — the field is in calendar days; a period counted otherwise is listed, never converted.
    settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
    settings_retry_subject: 'the framework deadlines',
    channels_retry_subject: 'the submission channels and deadlines',
    // Sprint 62.49.1 (DB-2, DB-4) — a row the server says owes no duty
    // (`RegulatoryDeadline.duty_not_owed`): the token, then the reason in words.
    // `{framework}` is the replacing framework's display name, never its code.
    token_not_owed: 'Not owed',
    not_owed_framework_status: 'The framework is not in force.',
    not_owed_row_suspended: 'This duty is suspended.',
    not_owed_superseded_by: 'Superseded by {framework}.',
    not_owed_superseded_unnamed: 'Superseded by another framework.',
    not_owed_row_legacy: 'Legacy — no longer in force.',
    not_owed_unrecognised: 'The record states that this duty is not owed.',
    settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
};
export const enFrameworkSuspension = {
    heading: 'Suspension',
    intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
    start_label: 'Began',
    start_value: 'Suspended from {date}',
    basis_label: 'Basis for that day',
    steps_caption: 'Instruments that suspended this framework, in the order they took effect',
    col_instrument: 'Instrument',
    col_issued: 'Issued',
    col_effective: 'Took effect',
    col_until: 'Suspends until',
    col_replaces: 'Replaces',
    replaces_none: 'None',
    until_date: 'Until {date}',
    source_line: 'Read from: {source}',
    end_label: 'Ends',
    end_date: 'On {date}',
    end_event: 'When this happens: {event}',
    event_not_occurred: 'Not yet occurred',
    event_occurred_on: 'Occurred on {date}',
};
export const enFrameworkBriefings = {
    summary_export: {
        title: 'Framework summary: {name}',
        section_status: 'Status and dates',
        col_field: 'Field',
        col_value: 'Value',
        field_code: 'Framework code',
        field_status: 'Status',
        field_issued: 'Issued',
        field_effective: 'In force from',
        draft_not_in_force: 'Draft — not yet in force',
        not_stated: 'Not stated',
        section_parent: 'Parent framework',
        parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
        parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
        no_parent: 'This framework names no parent framework.',
        parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
        col_belongs_to: 'Belongs to',
        section_guidance: 'Guidance',
        no_guidance: 'No guidance notes are installed for this framework.',
        col_title: 'Title',
        col_summary: 'Summary',
        section_requirements: 'Requirements',
        no_requirements: 'No requirements are installed for this framework.',
        col_requirement_id: 'ID',
        col_reference: 'Reference',
        col_addressed_to: 'Addressed to',
        col_mandatory: 'Mandatory',
        mandatory_yes: 'Yes',
        mandatory_no: 'No',
        mandatory_not_stated: 'Not stated',
        mandatory_no_draft: 'No — proposed in a draft',
        mandatory_no_legacy: 'No — the framework is legacy',
        mandatory_no_suspended: "No — the framework's application is suspended",
        section_deadlines: 'Deadlines',
        no_deadlines: 'No deadline rows are installed for this framework.',
        col_deadline: 'Deadline',
        col_date_or_period: 'Date or period',
        col_note: 'Note',
        stated_date: '{date} (as stated)',
        authority_clock: "The authority's period, not a duty of the organization.",
        section_pack: 'Pack',
        pack_line: 'Installed from pack {code}, version {version}.',
        pack_not_recorded: 'The pack this framework was installed from is not recorded.',
        generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
    },
    record: {
        tab: 'Briefings',
        heading: 'Briefings and consultation comments',
        intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
        boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
        kind_briefing: 'Briefing',
        kind_consultation_comment: 'Consultation comment',
        channel_meeting: 'Meeting',
        channel_email: 'Email',
        channel_document: 'Document',
        channel_webinar: 'Webinar',
        channel_other: 'Other',
        col_kind: 'Kind',
        col_audience: 'Audience',
        col_held_on: 'Date',
        col_channel: 'Channel',
        col_approver: 'Approver',
        col_approval: 'Approval',
        col_pack_version: 'Pack version',
        approval_none: 'No approver named',
        approval_pending: 'Awaiting approval',
        approval_approved: 'Approved on {date}',
        record: 'Record an entry',
        recording: 'Recording…',
        approve: 'Approve',
        approving: 'Approving…',
        approve_aria: 'Approve the entry of {date} for {audience}',
        field_kind: 'Kind',
        field_audience: 'Audience',
        field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
        field_held_on: 'Date held or sent',
        field_held_on_hint: 'A calendar date, not after today.',
        field_channel: 'Channel',
        field_channel_note: 'Channel details',
        field_channel_note_required: 'Describe the channel when it is Other.',
        field_reference: 'Reference',
        field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
        field_summary: 'Summary',
        field_approver: 'Approver',
        field_correction_of: 'Corrects entry',
        field_correction_note: 'Correction note',
        empty: 'No briefings or consultation comments are recorded for this framework.',
        load_failed: 'The briefings and consultation comments could not be loaded.',
        record_failed: 'The entry could not be recorded.',
        approve_failed: 'The entry could not be approved.',
        recorded_toast: 'Entry recorded.',
        approved_toast: 'Entry approved.',
        pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
        corrected_by: 'Corrected by a later entry',
        correction_label: 'Correction',
        evidence_count: 'Documents: {count}',
        attach_summary_offer: 'Attach this summary to an entry',
        kind_placeholder: 'Choose a kind',
        channel_placeholder: 'Choose a channel',
        approver_placeholder: 'No approver',
        correction_none: 'Not a correction',
        correction_option: '{kind} of {date} for {audience}',
        correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
        error_required: 'This field is required.',
        error_held_on_future: 'The date cannot be after today.',
        error_correction_note_required: 'Explain what this entry corrects.',
        error_approver_not_found: 'The approver is not a user of this organization.',
        error_not_approver: 'Only the named approver can approve this entry.',
        error_already_approved: 'This entry is already approved.',
        error_self_approval: 'The approver must be someone other than the person recording the entry.',
        form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
        summary_count: '{count} of {max} characters',
        loading: 'Loading the entries…',
        retry_subject: 'the entries',
        reload: 'Reload',
        reloading: 'Reloading…',
        pagination: 'Pages of entries',
        col_summary: 'Summary',
        col_documents: 'Documents',
        documents_open: 'Documents',
        documents_open_aria: 'Documents of the entry of {date} for {audience}',
        reference_line: 'Reference: {reference}',
        channel_note_line: 'Channel details: {note}',
        correction_note_line: 'Correction note: {note}',
        corrects_line: 'Corrects the entry of {date}',
        corrects_unknown: 'Corrects an earlier entry',
        pack_changed_open_comparison: 'See what changed',
        pack_changed_open_updates: 'Open Regulatory Updates',
        replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
        pack_version_not_recorded: 'Not recorded',
        recorded_while: 'Framework status when recorded: {status}',
        framework_status_suspended: 'Suspended',
        recorded_line: 'Recorded by {person} on {date}',
        files_heading: 'Documents',
        files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
        files_slot: 'Documents of this entry',
        files_none: 'No document is attached to this entry.',
        file_hash: 'SHA-256: {hash}',
        file_not_held: 'Document no longer held',
        file_download: 'Download',
        file_remove: 'Remove',
        file_upload: 'Add a document',
        file_bound: 'Document added.',
        file_removed: 'Document removed.',
        file_bind_failed: 'The document could not be added.',
        file_remove_failed: 'The document could not be removed.',
        file_not_owned: 'The entry this document belongs to was not found.',
        file_remove_confirm: 'Remove this document from the entry and delete it?',
        files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
        documents_load_failed: 'The documents could not be loaded.',
        export_label: 'Export summary',
        export_done: 'Framework summary exported.',
        export_failed: 'The framework summary could not be exported.',
        attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
        directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
        approver_retry_subject: 'the list of people',
    },
};
export const enRegulatoryUpdateComparison = {
    page: {
        module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
        title: 'What changed: {name}',
        title_fallback: 'What changed',
        description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
        back: 'Back to Regulatory Updates',
        loading: 'Loading the comparison…',
        load_failed: 'The comparison could not be loaded.',
        not_found: 'This regulatory update was not found.',
        retry_subject: 'the comparison',
        reload: 'Reload',
        reloading: 'Reloading…',
        no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
        status_heading: 'Status change',
        status_change: '{from} → {to}',
        replaced_by: 'Replaced by {name}',
        status_not_recorded: 'Not recorded',
        basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
        basis_declared: 'Compared using the correspondence the replacing framework declares.',
        derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
        from_heading: 'Earlier text',
        to_heading: 'Text installed now',
        side_status: 'Status: {status}',
        side_pack_version: 'Pack version: {version}',
        counts_heading: 'Summary of changes',
        count_line: '{label}: {count}',
        change_changed: 'Changed',
        change_removed: 'Removed',
        change_new: 'New',
        change_unchanged: 'Unchanged',
        rows_heading: 'Requirements',
        filter_label: 'Show',
        filter_all: 'All requirements',
        showing: 'Showing {shown} of {total}',
        col_requirement: 'Requirement',
        col_change: 'Change',
        col_from_title: 'Title in the earlier text',
        col_to_title: 'Title in the text installed now',
        col_mandatory: 'Mandatory',
        col_note: 'Note',
        mandatory_yes: 'Yes',
        mandatory_no: 'No',
        mandatory_not_stated: 'Not stated',
        mandatory_change: '{from} → {to}',
        not_in_text: 'Not in this text',
        also_corresponds: 'Also corresponds to: {ids}',
        rows_empty: 'No requirement matches this filter.',
        rows_pagination: 'Pages of requirements',
        superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
        withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
        withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
    },
    review: {
        heading: "Your organization's reading",
        intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
        record: 'Record comparison review',
        recording: 'Recording…',
        none: 'No comparison review is recorded yet.',
        current_heading: 'Current review',
        recorded_line: 'Recorded by {person} on {date}',
        conclusion: 'Conclusion',
        next_steps: 'Next steps',
        no_next_steps: 'No next steps recorded.',
        readings_heading: 'Readings',
        col_reading: 'Reading',
        reading_affects_us: 'Affects us',
        reading_does_not_affect_us: 'Does not affect us',
        reading_to_assess: 'To assess',
        no_rows_read: 'This review gives no reading for a single requirement.',
        history_heading: 'Earlier reviews',
        history_pagination: 'Pages of earlier reviews',
        col_recorded: 'Recorded',
        col_recorded_by: 'Recorded by',
        col_conclusion: 'Conclusion',
        col_readings: 'Readings',
        readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
        modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
        row_legend: '{id}: {title}',
        field_reading: 'Reading',
        field_reading_placeholder: 'Choose a reading',
        field_note: 'Note',
        field_conclusion: 'Conclusion',
        field_next_steps: 'Next steps',
        text_count: '{count} of {max} characters',
        error_reading_required: 'Choose a reading for this requirement.',
        error_conclusion_required: 'State your conclusion.',
        record_failed: 'The comparison review could not be recorded.',
        recorded_toast: 'Comparison review recorded.',
        no_comparison_error: 'This update holds no comparison to review.',
        rows_loading: 'Loading the requirements to read…',
        rows_load_failed: 'The requirements to read could not be loaded.',
        no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
    },
    reread: {
        heading: 'Records filled under the replaced text',
        intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
        forms_heading: 'Form records',
        col_record: 'Record',
        col_form: 'Form',
        col_status: 'Status',
        col_updated: 'Last updated',
        forms_none: 'No form record was filled under the replaced text.',
        forms_withheld: 'Form records are listed only to people who may read form records.',
        untitled: 'Untitled record',
        forms_pagination: 'Pages of form records',
        questionnaires_heading: 'Vendor questionnaires',
        notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
        notice_link: 'See what changed',
        notice_link_aria: 'See what changed between {from} and {to}',
        notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
        notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
        notice_same_text_link_aria: 'See what changed in {name}',
    },
    recheck: {
        will_draft: 'The pack publisher will recheck this draft on {date}.',
        will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
        will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
        was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
        was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
        was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
        records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
    },
    list: {
        col_changed: 'Changed requirements',
        no_comparison_short: 'No comparison',
        pagination: 'Pages of regulatory updates',
        view_comparison: 'View comparison',
        view_comparison_aria: 'View comparison for {title}',
        reviews_count: 'Reviews: {count}',
    },
};
export const enOrgIdentity = {
    nav_label: 'Organisation identity',
    module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
    title: 'Organisation identity',
    description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
    section_names: 'Names and offices',
    section_registration: 'Registration',
    section_contact: 'Contact details',
    section_representative: 'Legal representative',
    field_legal_name: 'Legal name',
    field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
    field_trading_name: 'Trading name',
    field_trading_name_help: 'A short or trading name, if the organisation uses one.',
    field_head_office_address: 'Head office address',
    field_transaction_office_address: 'Transaction office address',
    field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
    field_registration_number: 'Registration Number',
    field_registration_instrument: 'Registration document',
    field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
    field_registration_issuer: 'Issued by',
    field_registration_issue_date: 'Date of issue',
    field_registration_issue_place: 'Place of issue',
    field_tax_code: 'Tax Code',
    field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
    field_phone: 'Phone',
    field_email: 'Email',
    field_website: 'Website',
    field_website_help: 'Starting with https:// or http://.',
    field_legal_representative_name: 'Full name',
    field_legal_representative_title: 'Position',
    save: 'Save identity',
    saving: 'Saving…',
    saved: 'Organisation identity saved.',
    save_failed: 'The organisation identity could not be saved.',
    fix_fields: 'Some fields need correcting before the identity can be saved.',
    load_failed: 'The organisation identity could not be read.',
    retry: 'Retry',
    conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
    conflict_reload: 'Reload the saved identity',
    conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
    loading: 'Loading the organisation identity',
    read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
    restricted_title: 'You do not have access to the organisation identity',
    restricted_message: 'Ask an administrator for the settings read permission.',
    last_saved: 'Last saved {date}',
    never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
    error_too_long: 'This value is too long.',
    error_invalid_date: 'Enter a real date.',
    error_invalid_email: 'Enter an email address, for example name@example.com.',
    error_invalid_url: 'Enter a web address starting with https:// or http://.',
    error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
    error_invalid: 'This value is not accepted.',
};
export const enFormPrefill = {
    draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
    fill_from_record: 'Fill from record',
    fill_from_named_record: 'Fill from {record}',
    filling: 'Filling…',
    result: 'Filled {filled} fields; {differs} differ from the record.',
    failed: 'The form could not be filled from the record.',
    from_source: 'From {source}',
    not_recorded_in_source: 'Not recorded in {source}',
    family_organisation: 'the organisation identity',
    family_dpo: 'the DPO appointment',
    family_dpia: 'the DPIA',
    family_transfer: 'the transfer assessment',
    family_incident: 'the incident',
    family_dossier: 'the dossier filing',
    record_generic: 'the record',
    hint: 'Fills empty fields only. A value you entered is never replaced.',
};
export const enCustomsTaxExports = {
    titles: {
        dossiers: 'Customs tax dossiers and documents received',
        payments: 'Customs tax payments',
        duty_free_lists: 'Duty-free lists',
        examinations: 'Customs tax examinations',
        debt_instruments: 'Customs tax debt instruments',
        transition_statement: 'Transition statement',
    },
    sections: {
        dossiers: 'Dossiers',
        documents_received: 'Documents received',
        figures_side_by_side: 'Stated figures side by side',
        deductions: 'Deductions as customs recorded them',
        usage_notifications: 'Usage notifications recorded',
        steps: 'Steps',
        office_examinations: 'At the customs office',
        determinations: 'Determinations recorded',
        refund_records: 'Refund records',
        enforcement_notices: 'Enforcement notices received',
        instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
        noted_alternatives: 'Noted alternative readings of the usage schedule',
    },
    columns: {
        paper_form_number: 'Paper form number',
        electronic_indicator_set: 'Electronic indicator set',
        matched_by_title: 'Matched by title',
        issued_by_authority: 'Issued by the authority',
        dossier: 'Dossier',
        procedure: 'Procedure',
        register_role: 'Register role',
        customs_records: 'Customs records',
        channel: 'Channel',
        signed_off_on: 'Signed off on',
        lodged_on: 'Lodged on',
        confirmation_answer: 'Confirmation answer',
        install_mode_when_made: 'Install mode when made',
        review_approval: 'Review approval',
        document: 'Document',
        kind: 'Kind',
        number_as_printed: 'Number as printed',
        received_on: 'Received on',
        stated_figures: 'Stated figures',
        stated_deadlines: 'Stated deadlines',
        reply: 'Reply',
        payment: 'Payment',
        paid_on: 'Paid on',
        stated_amount: 'Stated amount',
        payer_class: 'Payer class',
        payment_date_basis: 'Payment-date basis',
        document_number: 'Document number',
        pays: 'Pays',
        receipt: 'Receipt',
        received_document: 'Received document',
        figure: 'Figure',
        payment_figure_as_stated: 'Payment figure (as stated)',
        document_figure_as_stated: 'Document figure (as stated)',
        comparison: 'Comparison',
        closed: 'Closed',
        list: 'List',
        reference: 'Reference',
        acknowledged_on: 'Acknowledged on',
        imports_ended_on: 'Imports ended on',
        expires_on: 'Expires on',
        deduction: 'Deduction',
        customs_record: 'Customs record',
        goods_as_stated: 'Goods as stated',
        unit_as_stated: 'Unit as stated',
        cleared_as_stated: 'Cleared (as stated)',
        remaining_as_stated: 'Remaining (as stated)',
        notification: 'Notification',
        notice_kind: 'Notice kind',
        financial_year_ended_on: 'Financial year ended on',
        notified_on: 'Notified on',
        examination: 'Examination',
        purpose: 'Purpose',
        decision_signed_on: 'Decision signed on',
        decision_sent_on: 'Decision sent on',
        announced_on: 'Announced on',
        re_examination_of: 'Re-examination of',
        step: 'Step',
        occurred_on: 'Occurred on',
        refused_to_sign: 'Refused to sign',
        record: 'Record',
        authority_request: 'Authority request',
        explanation_sent_on: 'Explanation sent on',
        outcome_on: 'Outcome on',
        instrument: 'Instrument',
        role: 'Role',
        effective_on: 'Effective on',
        instalment_band: 'Instalment band',
        approved_on: 'Approved on',
        stated_debt_figure: 'Stated debt figure',
        guarantee_valid_until: 'Guarantee valid until',
        record_type: 'Record type',
        governing_date: 'Governing date',
        outcome: 'Outcome',
        reading: 'Reading',
        open: 'Open',
        determination: 'Determination',
        determined_on: 'Determined on',
        refund_record: 'Refund record',
        dossier_register_role: 'Dossier register role',
        classification: 'Classification',
        decision_document: 'Decision document',
        offset_notice_document: 'Offset notice received',
        post_refund_examination: 'Post-refund examination',
        stated_case: 'Case as the decision states it',
        stated_case_source: 'Case stated in',
        stated_case_citation: 'Provision cited for the case',
        received_as: 'Received as',
        document_date: 'Document date',
        amount_stated_for_notice: 'Amount stated for this notice',
        financial_year_reported: 'Financial year reported (ending)',
        noted_alternative_year: 'Noted alternative financial year (ending)',
        note: 'Note',
        reading_kind: 'Kind of reading',
        classification_case: 'Case the classification notice states',
        acceptance_notice: "Acceptance notice (the authority's clock)",
        handling_time: "Handling time (the authority's clock)",
        stated_case_choices: 'Installed cases matched (as stated)',
        stated_due_on: 'Due date as stated',
        period_ceiling: 'Longest instalment period',
        stated_plan_end: 'Latest end the Law allows',
        enforcement_from: "Enforcement threshold (the authority's clock)",
    },
    cells: {
        review_approved: 'Approved by {name} on {date}',
        review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
        register_role_refund: 'Refund',
        register_role_overpaid_refund: 'Refund of an overpaid amount',
        noted_not_due: 'Noted reading, not a due date',
        received_as_third_party: 'Received as a third party',
        received_as_enforced_taxpayer: 'The taxpayer under enforcement',
        clock_authority_ends: "The authority's clock ends {date} ({clock})",
        clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
        clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
        clock_period_ends: 'The period ends {date} ({clock})',
        clock_closed: 'Closed on {date}',
        clock_not_applicable: 'Not applicable to this record ({clock})',
        clock_not_live: 'Not in force yet ({clock})',
        clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
        clock_not_declared: 'No installed clock declares it',
        clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
        clock_no_date: 'No date ({clock})',
        plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
        plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
        clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
        clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
        meta_boundary: 'Boundary ({framework}): {summary}',
        meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
        meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
        meta_statement_for: 'Statement for: {date}',
        meta_nothing_remains: 'Nothing remains open under the earlier rules.',
        meta_records_remain: 'Records remain open under the earlier rules.',
        meta_transition_unavailable: 'The transition reading is not available on this installation.',
        role_notified_debt: 'Tax debt as notified',
        role_freeze: 'Freeze',
        role_freeze_end: 'End of a freeze',
        role_write_off: 'Write-off',
        role_restoration: 'Restoration',
        role_instalment_request: 'Instalment request',
        role_instalment_notice: 'Instalment notice',
        received_document_label: '{kind} {number}, dated {date}',
        received_document_label_no_number: '{kind}, received {date}',
        case_no_longer_installed: 'A case from an instrument that is no longer installed',
    },
};
export const enCrossBorderFilingFence = {
    register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
    refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
    open_register: 'Open {register}',
    option_owned_elsewhere: '{form} — recorded in {register}',
    picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
    advisory_title: 'This form is now recorded in {register}',
    advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
    list_marker: 'Now recorded in {register}',
};
export const enSectorOverlaysModule = {
    nav: {
        overview: 'Overview',
    },
    overview: {
        title: 'Sector Overlays',
        subtitle: 'What your active sector overlays bring into use, grouped by sector.',
        loading: 'Loading sector overlays…',
        load_failed: 'Sector overlays could not be loaded.',
        // fix-sprint-20261102-0448 F5 — the server answered PACKAGE_LIMIT.
        not_entitled_title: 'Sector Overlays is not in your package',
        not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
        empty_title: 'No sector overlay is active',
        empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
        open_catalog: 'Open the Overlay Catalog',
        organization_profile: 'Organization profile',
        organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
        provided_by: 'Provided by',
        views_heading: 'Views in this sector',
        shared_views_heading: 'Shared views this sector uses',
        shared_view_location: 'In {location}',
        // Starts with the visible link text, {view} (WCAG 2.5.3); gate E12.5 P3-4.
        open_view_aria: '{view} — {location}',
        no_visible_views: "None of this sector's views is open to your role.",
        sector_count_one: '1 overlay active',
        sector_count: '{count} overlays active',
    },
};
export const enProductIdentification = {
    common: {
        retry: 'Retry',
        loading: 'Loading…',
        cancel: 'Cancel',
        close: 'Close',
        no_access_title: 'No access',
        no_access_message: 'Your role cannot read this register.',
        install_direct: 'Installed Direct',
        install_reference: 'Installed for reference',
        reference_mode_body: 'The overlay that declares this register is installed for reference. Scope determinations can still be recorded and read here, and the sector watch read; no duty and no clock is run from them until the overlay is installed Direct from the Overlay Catalog.',
    },
    participation: {
        compulsory: 'Compulsory',
        voluntary: 'Voluntary',
        outside_scope: 'Outside the scope',
        compulsory_rules_disapplied: 'Compulsory rules disapplied',
        not_determined: 'Open determination',
        unclassified: 'No class declared',
    },
    scope: {
        title: 'Product Scope Determinations',
        subtitle: 'A person’s recorded reading, per product line, of whether the identification and traceability regime reaches it, with the instrument relied on and a second person’s review. This register records the reading; it never decides it.',
        page_guide: 'Record one determination per product line from the options the installed pack declares. Nothing is preselected or suggested. Recording again keeps the earlier version, and compulsory and voluntary lines are always counted separately.',
        load_failed: 'The scope determinations could not be loaded.',
        none_declared: 'No installed framework declares the scope-determination register.',
        applicability_heading: 'Who the regime reaches, as the installed pack states it',
        mandatory_scope: 'Compulsory',
        voluntary_scope: 'Voluntary',
        out_of_scope: 'Outside the scope',
        mandatory_rules_disapplied: 'Compulsory rules disapplied',
        list_status_note: 'Status of the lists',
        counts_heading: 'Product lines by class',
        filter_label: 'Class',
        filter_all: 'All classes',
        table_caption: 'Current determination per product line',
        col_product: 'Product line',
        col_class: 'Class',
        col_option: 'Determination',
        col_instrument: 'Instrument relied on',
        col_determined_by: 'Determined by',
        col_reviewed_by: 'Reviewed by',
        col_determined_on: 'Determined on',
        col_version: 'Version',
        col_actions: 'Actions',
        empty: 'No determination is recorded yet.',
        empty_filtered: 'No product line is in this class.',
        record_button: 'Record a determination',
        record_again: 'Record again',
        history_button: 'History',
        duties_button: 'Duties',
        form_title: 'Record a scope determination',
        field_product: 'Product line',
        field_framework: 'Framework',
        choose: 'Choose…',
        no_default_note: 'Nothing is preselected and nothing is suggested: the reading is yours.',
        instrument_required: 'Required for the determination chosen.',
        cited_basis_note_heading: 'The installed pack’s reading of the lists',
        reviewer_same: 'The reviewer must be a person other than the one who determined it.',
        field_required: 'This field is required.',
        save: 'Save',
        saving: 'Saving…',
        save_failed: 'The determination could not be saved.',
        saved: 'Determination recorded.',
        products_failed: 'The product lines could not be loaded.',
        no_products: 'No product line is in the product register yet.',
        conflict: 'Someone recorded a determination for this line meanwhile. Reload the register and try again.',
        history_title: 'Determination history — {product}',
        history_failed: 'The history could not be loaded.',
        status_current: 'Current',
        status_superseded: 'Superseded',
        duties_title: 'Duties for {product}',
        duties_failed: 'The duties could not be loaded.',
        duties_no_task: "No task or deadline is created here from these duties. Where the decree states a period, the requirement's own text states it.",
        duties_reference: 'Installed for reference: no duty is shown. Install the overlay Direct to run them.',
        duties_compulsory_heading: 'Compulsory',
        duties_voluntary_heading: 'Voluntary',
        duties_role_heading: 'Attached to a role',
        duties_open: 'Open determination: the scope of this line is still to be determined. This is not a finding that no duty applies.',
        duties_counts: 'Compulsory: {applies} · Applies to how you take part (voluntary): {voluntary}',
        duties_none: 'No requirement is declared.',
        col_requirement: 'Requirement',
        col_state: 'Status for this line',
        col_reference: 'Reference',
    },
    duty_state: {
        applies: 'Applies (compulsory)',
        applies_voluntarily: 'Applies to how you take part (voluntary)',
        not_raised_by_option: 'Not raised by this determination',
        open_determination: 'Open determination',
        role_attached: 'Attached to a role; read its condition',
        voluntary_practice: 'Voluntary practice',
    },
    watch: {
        title: 'Sector Watch',
        subtitle: 'What the installed pack tracks as still to be issued by an authority, with the status it records and when it must be checked again, and the clocks the pack places on authorities. Nothing here is owed by your organisation.',
        page_guide: 'Read each tracked output as the installed pack states it. A recheck date that has passed reads as recheck overdue; nothing is re-dated. The clocks on authorities run on the authority, not on your organisation.',
        load_failed: 'The sector watch could not be loaded.',
        items_heading: 'Outputs still to be issued',
        items_caption: 'Tracked outputs, as the installed pack states them',
        col_output: 'Output',
        col_status: 'Status',
        col_basis: 'Basis of the status',
        col_checked: 'Checked on',
        col_recheck: 'Recheck due',
        col_install: 'Install mode',
        status_not_issued: 'Not issued',
        status_not_retrieved: 'Named, text not retrieved',
        status_announced_operational: 'Announced operational',
        status_issued: 'Issued',
        recheck_overdue: 'Recheck overdue',
        existing_basis: 'Instruments recorded as the existing basis',
        note_label: 'Note',
        items_none: 'No installed framework tracks an output for this register.',
        clocks_heading: 'Clocks on authorities and periods not stated',
        clocks_caption: 'Records the installed pack places on authorities, or whose period it does not state',
        col_clock: 'Record',
        col_kind: 'What it means for your organisation',
        col_period: 'Period as stated',
        col_binds: 'Applies from',
        authority_clock: 'Clock on the ministry — nothing is owed by the organisation',
        set_by_instrument: 'Set by the ministry instrument or sector law',
        no_period: 'No period stated',
        period_months: '{n} months',
        period_days: '{n} days',
        period_other: '{n} ({unit})',
        not_stated: 'Not stated',
        clocks_none: 'No clock on an authority is declared.',
    },
};
/**
 * Sprint 62.57.0 Slice E (stream F) — the processing-service certificate
 * register (work file §12.6, B0 naming §12.6.2). Every legal sentence — the
 * scope text, out-of-scope items, validity note, part and ground labels,
 * period labels, precautions, citations, the boundary statement — is pack text
 * read from the API, never a key here (R-46). Vietnamese: R-44 batch
 * `2026-11-03-62.57.0-e` (under review). The 14 other locales carry this
 * English (R-57).
 */
export const enServiceCertificates = {
    nav: 'Processing Service Certificate',
    common: {
        loading: 'Loading…',
        load_failed: 'This could not be read.',
        retry: 'Try again',
        reloading: 'Reading again…',
        recording: 'Recording…',
        none: '—',
        not_recorded: 'Not recorded',
        unknown_user: 'Unknown person',
        yes: 'Yes',
        no: 'No',
        close: 'Close',
        record_changed: 'Someone changed this record since you opened it. It has been read again and your entries are kept: check them and save again.',
        save_failed: 'This was not recorded.',
        scope_not_stated: 'Your organization’s statement on the Organization profile names no service this certificate concerns, so a new record cannot be created.',
        recorded_by: 'Recorded by {person} on {date}',
        not_verified: 'Recorded by {person} — ComplianceOne has not verified it',
        corrects: 'Corrects the entry of {date}',
        corrected_by_later: 'Corrected by a later entry',
        correction_note: 'Correction note: {note}',
        field_correction_note: 'Why this is corrected',
        field_corrects: 'Entry this corrects',
        corrects_none: 'Not a correction — a new entry',
        error_required: 'This is required.',
        error_future: 'This date is later than today.',
        error_date_order: 'This date is earlier than the date it follows.',
        error_field_refused: 'Check this field.',
        error_correction_note_required: 'Say why the entry is corrected.',
        owner: 'Accountable owner',
        owner_placeholder: 'Choose a person',
        owner_retry_subject: 'the list of people',
        files: 'Files: {count}',
        files_none: 'No file',
        files_open: 'Files',
        files_heading: 'Files',
        files_intro: 'Files your organization holds for this entry. ComplianceOne keeps them as they were uploaded and does not read them.',
        file_hash: 'SHA-256',
        file_not_held: 'This file is no longer held.',
        file_download: 'Download',
        file_remove: 'Remove',
        file_upload: 'Upload a file',
        file_bound: 'The file was added.',
        file_removed: 'The file was removed.',
        file_bind_failed: 'The file could not be added.',
        file_remove_failed: 'The file could not be removed.',
        file_not_owned: 'This file does not belong to this record.',
        file_remove_confirm: 'Remove this file from the record?',
        export_done: 'The export is ready.',
        export_failed: 'The export could not be produced.',
        history: 'History',
        history_none: 'No change is recorded.',
        history_col_event: 'What changed',
        history_col_changes: 'Fields',
        history_col_recorded: 'Recorded',
        history_pagination: 'History pages',
        history_retry_subject: 'the history',
        boundary_heading: 'What this register is',
        pack_text_from: 'From the installed framework',
    },
    register: {
        title: 'Processing Service Certificate',
        subtitle: 'Your organization’s record of the certificate of eligibility it holds or applies for, and of what the authority wrote about it.',
        module_guide: 'Record the certificate your organization holds, open an application for one, and keep what the authority sends. The register is shown once your organization has stated on its Organization profile that it provides a listed personal data processing service. ComplianceOne decides nothing for the authority.',
        scope_heading: 'Scope',
        scope_intro: 'Who the certificate concerns, as the installed framework states it.',
        out_of_scope_heading: 'Not within the scope, as the installed framework reads it',
        statement_heading: 'Your organization’s statement',
        statement_services: 'Services stated: {list}',
        statement_none: 'Your organization has stated that it provides none of the listed services.',
        statement_unknown: 'Your organization has stated that it does not know.',
        statement_not_stated: 'Nothing is recorded on the Organization profile.',
        statement_outside: 'The services stated are not among those this certificate concerns.',
        statement_by: 'Stated by {person} on {date} · Evidence: {count}',
        position_line: 'Services stated: {list} · Certificates recorded: {certificates} · Applications open: {applications}',
        profile_link: 'Change on the Organization profile',
        not_offered: 'This register is shown once your organization has recorded, on its Organization profile, that it provides one of the listed personal data processing services. Nothing is inferred from anything else.',
        cannot_create: 'New certificates and applications are recorded only while your organization’s statement names a listed service. Existing records stay readable and can still be recorded on.',
        availability_failed: 'Whether this register is offered could not be read.',
        availability_retry_subject: 'the register’s availability',
        declarations_failed: 'The installed framework’s declaration could not be read.',
        declarations_retry_subject: 'the installed declaration',
        certificates_heading: 'Certificates',
        applications_heading: 'Applications',
        record_certificate: 'Record a certificate held',
        open_application: 'Open an application',
        export: 'Export the register',
        certificates_none: 'No certificate is recorded.',
        applications_none: 'No application is recorded.',
        certificates_failed: 'The certificates could not be read.',
        certificates_retry_subject: 'the certificates',
        applications_failed: 'The applications could not be read.',
        applications_retry_subject: 'the applications',
        certificates_pagination: 'Certificate pages',
        applications_pagination: 'Application pages',
        col_number: 'Certificate number',
        col_valid_from: 'Valid from (as stated on the certificate)',
        col_authority: 'Issuing authority as named on the certificate',
        col_services: 'Services',
        col_status: 'Status',
        col_owner: 'Accountable owner',
        col_application: 'Application',
        col_reference: 'Reference',
        col_governing_text: 'Governing text',
        col_latest_communication: 'Latest communication',
        col_next_period: 'Next period',
        end_date_stated: 'The certificate states an end date',
        return_outstanding: 'Return of the certificate not recorded',
        governing_not_determined: 'Not determined — the receipt is not recorded',
        open_certificate: 'Open certificate {number}',
        open_application_row: 'Open the application: {kind}, {date}',
        certificate_recorded: 'The certificate was recorded.',
        application_opened: 'The application was opened.',
    },
    certificate_status: {
        current: 'Current',
        replaced: 'Replaced',
        revoked: 'Revoked',
    },
    application_status: {
        preparing: 'Preparing',
        filed: 'Filed',
        decided: 'Decided',
        withdrawn: 'Withdrawn',
    },
    application_kind: {
        issue: 'Issue',
        replacement: 'Re-issue or exchange',
    },
    document_kind: {
        certificate_paper: 'Certificate, paper copy',
        certificate_electronic: 'Certificate, electronic copy',
        issue_decision: 'Issue decision',
        revocation_decision: 'Revocation decision',
        dossier_as_filed: 'Dossier as filed',
        other: 'Other document',
    },
    part_state: {
        provided: 'Provided',
        not_provided: 'Not provided',
        not_required_exception: 'Not required — {exception}',
    },
    decision_outcome: {
        issued: 'Issued',
        refused: 'Refused',
    },
    comm_kind_decision: 'Decision',
    validity: {
        valid_from: 'Valid from (as stated on the certificate)',
        no_end_date: 'No end date stated',
        states_end_date: 'The certificate states an end date',
        end_date: 'End date as stated on the certificate',
        end_date_note: 'ComplianceOne computes nothing from this date.',
    },
    authority: {
        as_named: 'Issuing authority as named on the certificate',
        declared: 'Issuing authority (from the installed framework)',
        declared_none: 'The installed framework names none',
    },
    certificate: {
        back: 'Back to the register',
        heading: 'Certificate {number}',
        not_found: 'This certificate is not recorded in your organization’s register.',
        load_failed: 'The certificate could not be read.',
        retry_subject: 'the certificate',
        facts_heading: 'Certificate',
        number: 'Certificate number',
        services: 'Services covered',
        forms_held: 'Held as',
        paper: 'Paper',
        electronic: 'Electronic',
        notes: 'Notes',
        status: 'Status',
        supersedes: 'Replaces an earlier certificate',
        superseded_by: 'Replaced by a later certificate',
        issued_by_application: 'Recorded from the decision on an application',
        open_linked: 'Open',
        correct: 'Correct the certificate',
        corrected: 'The correction was recorded.',
        export: 'Export',
        documents_heading: 'Documents',
        add_document: 'Add a document',
        documents_none: 'No document is held.',
        document_col_kind: 'Kind',
        document_col_file: 'File',
        document_col_added: 'Added',
        document_added: 'The document was added.',
        revocation_heading: 'Revocation',
        record_revocation: 'Record a revocation',
        revocations_none: 'No revocation is recorded.',
        decision_number: 'Decision number',
        decision_date: 'Decision date',
        ground: 'Ground',
        ground_not_declared: 'A ground the installed framework no longer declares',
        effective_on: 'Effective on',
        decision_received_on: 'Decision received on',
        voluntary_request: 'Requested by your organization on {date}',
        revocation_recorded: 'The revocation was recorded.',
        return_heading: 'Return of the certificate',
        record_return: 'Record the return',
        returns_none: 'No return is recorded.',
        returned_on: 'Returned on',
        return_recorded: 'The return was recorded.',
        return_files_heading: 'Return of the certificate — files',
        publication_heading: 'Publication seen',
        record_publication: 'Record a publication seen',
        publications_none: 'No publication is recorded.',
        seen_on: 'Seen on',
        reference_text: 'Where it was seen',
        publication_recorded: 'The publication was recorded.',
        period_heading: 'Period to return the certificate',
        communications_heading: 'Authority communications',
    },
    application: {
        back: 'Back to the register',
        heading: '{kind}: {label}',
        not_found: 'This application is not recorded in your organization’s register.',
        load_failed: 'The application could not be read.',
        retry_subject: 'the application',
        facts_heading: 'Application',
        kind: 'Application',
        status: 'Status',
        reference: 'Application reference',
        services_applied_for: 'Services applied for',
        replaces: 'Replaces certificate',
        grounds: 'Grounds',
        ground_review: 'Not a ground under the governing text — review',
        ground_windows: 'Offered under: {windows}',
        notes: 'Notes',
        edit: 'Edit the application',
        updated: 'The application was updated.',
        export: 'Export',
        withdraw: 'Withdraw the application',
        withdrawn_on: 'Withdrawn on {date}',
        withdrawn: 'The application was withdrawn.',
        parts_heading: 'Dossier parts',
        parts_unstamped: 'Which parts are required depends on the date the authority receives the application',
        parts_stamped: 'The parts the governing text lists for this procedure.',
        parts_window: '{window} — {procedure}',
        parts_col_part: 'Part',
        parts_col_state: 'State',
        parts_col_owner: 'Owner and your organization’s own date',
        parts_col_files: 'Files',
        parts_col_action: 'Action',
        part_not_recorded: 'Not recorded',
        record_part: 'Record',
        record_part_label: 'Record the part: {part}',
        part_form: 'Form: {form}',
        part_recorded: 'The part was recorded.',
        own_date: 'Your organization’s own date: {date}',
        conditions_heading: 'Impact-assessment results',
        result_as_recorded: 'Result as recorded: {label}',
        result_none: 'No result is recorded on this filing',
        link_filings: 'Link filings',
        link_filings_label: 'Link filings: {condition}',
        conditions_none: 'No filing is linked.',
        conditions_recorded: 'The links were recorded.',
        open_filing: 'Open the filing',
        filing_heading: 'Filing',
        record_filing: 'Record the filing',
        filing_none: 'The application has not been filed.',
        filed_on: 'Filed on',
        channel: 'Channel',
        channel_not_declared: 'A channel the installed framework no longer declares',
        reviewed_by: 'Reviewed by',
        signed_by: 'Signed by',
        signed_on: 'Signed on',
        filing_recorded: 'The filing was recorded.',
        receipt_recorded: 'The receipt was recorded.',
        receipt_no_deficiency: 'No deficiency notice — the application as received was complete',
        periods_heading: 'Periods',
        periods_none: 'No period applies yet.',
        deficiency_heading: 'Deficiency notices and supplements',
        record_request: 'Record a deficiency notice',
        requests_none: 'No deficiency notice is recorded.',
        request_from: 'Notice received on {date}',
        request_items: 'Items',
        request_period: 'Period to supplement, as the notice states it: {text}',
        no_period: 'No period stated in the notice',
        stated_deadline: 'Date entered from the notice: {date}',
        item_owner: 'Owner: {person}',
        request_recorded: 'The deficiency notice was recorded.',
        supplements_heading: 'Supplements sent',
        record_sent: 'Record a supplement sent',
        sent_none: 'No supplement is recorded as sent.',
        sent_on: 'Sent on',
        sent_for: 'For the notice received on {date}',
        sent_recorded: 'The supplement was recorded.',
        supplement_files_heading: 'Supplements sent — files',
        evaluation_heading: 'Evaluation',
        record_evaluation: 'Record the evaluation',
        evaluation_none: 'No evaluation is recorded.',
        evaluation_not_declared: 'The governing text declares no evaluation for this procedure.',
        evaluation_recorded_no_decision: 'Evaluation recorded: {label} — no decision recorded',
        evaluation_recorded: 'The evaluation was recorded.',
        different_text: 'Recorded under a different governing text — review',
        notice_date: 'Notice date',
        notice_received_on: 'Notice received on',
        option_not_declared: 'An option the installed framework no longer declares',
        decision_heading: 'Decision',
        record_decision: 'Record the decision',
        decision_none: 'No decision is recorded.',
        outcome: 'Decision',
        reasons: 'Reasons as stated',
        issued_certificate: 'Certificate recorded from this decision',
        decision_recorded: 'The decision was recorded.',
        communication: 'Communication',
        communications_heading: 'Authority communications',
    },
    periods: {
        col_period: 'Period',
        col_whose: 'Whose period',
        col_date: 'Date',
        col_basis: 'Counted from',
        authority: 'The authority’s period — indicative',
        organization: 'Your organization’s period',
        not_stated: 'Whose period it is is not stated',
        provisional: 'Provisional — start event pending legal confirmation',
        passed: 'Period passed — no result recorded',
        counted_on: 'Counted on: {calendar}',
        no_date: 'No date — {reason}',
        guidance: 'Guidance: {precaution}',
        counts_from: '{event} on {date}',
        counts_from_not_evidenced: '{event} — not recorded',
        declared: '{count} {unit}',
        trigger: {
            application_received: 'Receipt of the application',
            complete_dossier_received: 'Receipt of the complete and valid dossier',
            replacement_request_received: 'Receipt of the request',
            deficiency_notice_received: 'Receipt of the deficiency notice',
            revocation_decision_received: 'Receipt of the revocation decision',
            not_stated: 'The text states no start',
        },
        reason: {
            owner_not_stated: 'the text does not say whose period it is',
            governing_text_not_determined: 'the receipt of the application is not recorded',
            trigger_not_evidenced: 'the event it counts from is not recorded',
            uncomputable_period: 'the period cannot be counted as declared',
            statutory_calendar_not_declared: 'no public-service calendar is named',
            statutory_calendar_unavailable: 'the public-service calendar is not installed',
            statutory_calendar_not_covering: 'the public-service calendar for that year is not installed',
        },
        unit: {
            working_days: 'working days',
            calendar_days: 'calendar days',
            days: 'days',
        },
    },
    receipt: {
        no_deficiency_notice: 'No deficiency notice — the application as received was complete',
        no_deficiency_hint: 'Records the complete dossier as received on {date}, the date the authority received the application. Choose this only if the authority sent no notice that the dossier was incomplete.',
        no_deficiency_blocked: 'A deficiency notice is recorded on this application, so this choice is not offered.',
        no_deficiency_needs_receipt: 'Record the authority’s receipt of the application first.',
        no_deficiency_date_mismatch: 'With this choice, the date is the date the authority received the application.',
    },
    modals: {
        certificate_title_create: 'Record a certificate held',
        certificate_title_correct: 'Correct the certificate',
        certificate_intro: 'Enter the certificate as it is written. ComplianceOne records what you enter and has not verified it.',
        kind: 'Certificate',
        services: 'Services covered',
        services_hint: 'The services the certificate lists, as the installed framework names them.',
        forms_held_legend: 'Held as',
        issuing_authority_hint: 'Exactly as the certificate names it.',
        notes: 'Notes',
        document_title: 'Add a document',
        document_kind: 'Kind of document',
        document_file: 'File',
        document_file_required: 'Choose a file.',
        revocation_title: 'Record a revocation',
        revocation_intro: 'Record the revocation decision as the authority wrote it. Recording it marks the certificate revoked and raises a task to return it.',
        revocation_communication: 'The decision as recorded among the communications',
        voluntary_legend: 'Requested by your organization',
        voluntary_requested: 'Your organization asked for the revocation',
        voluntary_requested_on: 'Requested on',
        voluntary_note: 'Note on the request',
        return_title: 'Record the return of the certificate',
        return_note: 'Note',
        publication_title: 'Record a publication seen',
        publication_reference_hint: 'Where your organization saw the revocation announced, as you would cite it.',
        application_title: 'Open an application',
        application_intro: 'An application is your organization’s record of what it files and what the authority does with it.',
        application_kind_legend: 'What the application asks for',
        replaces: 'Certificate it replaces',
        replaces_hint: 'A current certificate.',
        replaces_none: 'No current certificate is recorded.',
        grounds_legend: 'Grounds',
        grounds_hint: 'Each ground is shown with the governing texts that offer it.',
        ground_option: '{ground} — {windows}',
        reference: 'Application reference',
        reference_hint: 'Your own reference, or the authority’s once it gives one.',
        application_edit_title: 'Edit the application',
        part_title: 'Record the part: {part}',
        part_state_legend: 'State',
        part_form: 'Form instance',
        part_form_hint: 'A form your organization filled in.',
        part_owner: 'Owner',
        part_internal_due: 'Your organization’s own date',
        part_internal_due_hint: 'An internal date your organization sets. ComplianceOne proposes none.',
        part_note: 'Note',
        conditions_title: 'Link filings: {condition}',
        conditions_intro: 'Link your organization’s impact-assessment filings. Each shows its recorded result as recorded; ComplianceOne reads none of them as meeting the condition.',
        conditions_filings: 'Filings',
        conditions_none_offered: 'No filing of the kinds this condition names is recorded.',
        filing_title: 'Record the filing',
        filing_channel: 'Channel',
        filing_reviewed_by: 'Reviewed by',
        filing_signed_by: 'Signed by (name)',
        filing_signed_on: 'Signed on',
        request_title: 'Record a deficiency notice',
        request_intro: 'Record the notice as the authority wrote it. Your organization’s period to supplement is the one the notice states.',
        request_communication: 'The notice as recorded among the communications',
        request_communication_hint: 'Record the notice under Authority communications first, as a supplement request.',
        request_items_legend: 'Items the notice asks for',
        request_item: 'Item {n}',
        request_item_owner: 'Owner of item {n}',
        request_add_item: 'Add an item',
        request_remove_item: 'Remove item {n}',
        request_item_added: 'Item {n} added.',
        request_item_removed: 'Item {n} removed.',
        request_items_required: 'Add at least one item.',
        request_period: 'Period to supplement, as the notice states it',
        request_period_hint: 'Copy the period exactly as the notice states it, with its start if the notice gives one. Your organization’s period to supplement is the one the notice states, recorded by a person. ComplianceOne computes no supplement deadline.',
        request_deadline: 'Date the notice states',
        request_deadline_hint: 'Only if the notice states a date. Enter it as the notice gives it.',
        sent_title: 'Record a supplement sent',
        sent_request: 'Deficiency notice',
        sent_note: 'Note',
        evaluation_title: 'Record the evaluation',
        evaluation_intro: 'An evaluation is not a decision. Record the decision separately when the authority issues it.',
        evaluation_option: 'Evaluation result',
        evaluation_options_none: 'The governing text is not determined, so no evaluation result is offered. Record the authority’s receipt of the application first.',
        evaluation_communication: 'The notice as recorded among the communications',
        decision_title: 'Record the decision',
        decision_outcome_legend: 'The authority’s decision',
        decision_number: 'Decision number',
        decision_reasons: 'Reasons as stated',
        decision_reasons_hint: 'Copy the reasons exactly as the decision states them.',
        decision_certificate_legend: 'The certificate as the decision issues it',
        decision_communication: 'The decision as recorded among the communications',
        withdraw_title: 'Withdraw the application',
        withdraw_on: 'Withdrawn on',
        withdraw_reason: 'Reason',
        communication_pick_empty: 'No communication is recorded on this record.',
        communication_pick_failed: 'The communications could not be read.',
        communication_pick_manual_label: 'Communication id',
        communication_pick_manual_hint: 'Choose it from the list once it can be read.',
        communication_pick_off_list: 'A communication not on the first page ({value})',
        communication_pick_retry_subject: 'the communications',
        communication_option: '{kind}, received {date}',
        filing_pick_empty: 'No filing is recorded.',
        filing_pick_failed: 'The filings could not be read.',
        filing_pick_manual_label: 'Filing id',
        filing_pick_manual_hint: 'Choose it from the list once it can be read.',
        filing_pick_off_list: 'A filing not on the first page ({value})',
        filing_pick_retry_subject: 'the filings',
        form_pick_empty: 'No form instance is recorded.',
        form_pick_failed: 'The form instances could not be read.',
        form_pick_manual_label: 'Form instance id',
        form_pick_manual_hint: 'Choose it from the list once it can be read.',
        form_pick_off_list: 'A form instance not on the first page ({value})',
        form_pick_retry_subject: 'the form instances',
    },
    errors: {
        kind_not_declared: 'The installed framework does not declare this certificate.',
        service_not_declared: 'A service chosen is not one the installed framework lists.',
        owner_not_in_organisation: 'This person is not a member of your organization.',
        document_kind_invalid: 'Choose a kind of document.',
        already_revoked: 'A revocation is already recorded. Correct it instead.',
        not_revoked: 'The certificate is not recorded as revoked.',
        ground_not_declared: 'The ground chosen is not one the installed framework declares.',
        communication_invalid: 'Choose a communication of the right kind recorded on this record.',
        replaces_invalid: 'Choose a current certificate.',
        application_decided: 'A decision is recorded on this application.',
        application_not_preparing: 'The application is already filed.',
        application_not_filed: 'The application is not filed yet.',
        part_not_declared: 'This part is not declared by any governing text.',
        part_exception_not_declared: 'This part declares no exception.',
        form_instance_invalid: 'Choose a form instance of this part’s form.',
        condition_link_not_declared: 'This condition is not declared.',
        filing_invalid: 'Choose filings of the kinds this condition names.',
        channel_not_declared: 'Choose a channel the installed framework declares.',
        supplement_request_not_found: 'Choose a recorded deficiency notice.',
        evaluation_not_declared: 'The governing text declares no evaluation for this procedure.',
        governing_text_not_determined: 'Record the authority’s receipt of the application first.',
        evaluation_option_not_offered: 'Choose an option the governing text offers.',
        replaced_certificate_not_current: 'The certificate this replaces is no longer current.',
        decision_reasons_required: 'Copy the reasons the decision states.',
        decision_certificate_required: 'Enter the certificate the decision issues.',
        not_offered: 'This register is not offered to your organization.',
    },
};
/**
 * Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor record. No
 * label calls the record a *check* (`legal` §R.5); the basis, its note, the
 * boundary statement and the service items are pack text (R-46).
 */
export const enVendorServiceCertificate = {
    action: 'Processing services and certificate',
    action_label: 'Processing services and certificate: {vendor}',
    title: '{vendor}: processing services and certificate',
    loading: 'Loading…',
    load_failed: 'The record could not be read.',
    retry: 'Try again',
    retry_subject: 'the vendor’s record',
    reloading: 'Reading again…',
    basis_heading: 'Why your organization may record this',
    statement_legend: 'Personal data processing services this vendor provides on your organization’s behalf',
    state_selected: 'Provides these services',
    state_none: 'Provides none of them',
    state_unknown: 'Not known',
    not_recorded: 'Not recorded',
    current_statement: 'Current statement: {state}',
    stated_by: 'Stated by {person} on {date}',
    activities_legend: 'Services',
    activities_required: 'Choose at least one service.',
    statement_note: 'Note',
    save_statement: 'Save the statement',
    statement_saved: 'The statement was saved.',
    certificate_heading: 'The vendor’s certificate, as the vendor states or shows it',
    record_entry: 'Record what the vendor stated',
    correct_entry: 'Correct this entry',
    correct_entry_label: 'Correct the entry of {date}',
    entry_saved: 'The entry was recorded.',
    entries_none: 'Nothing is recorded about the vendor’s certificate.',
    entries_heading: 'Entries',
    entries_pagination: 'Entry pages',
    entries_failed: 'The entries could not be read.',
    entries_retry_subject: 'the entries',
    position_legend: 'What the vendor stated',
    position: {
        holds: 'Holds a certificate',
        applying: 'Applying',
        does_not_hold: 'Does not hold one',
        declined_to_state: 'Declined to state',
    },
    source: 'How the vendor stated it',
    sources: {
        written_statement: 'Written statement',
        document_shown: 'Document shown',
        contract_clause: 'Contract clause',
        other: 'Other',
    },
    stated_on: 'Stated on',
    number: 'Certificate number as stated',
    issuing_body: 'Issuing body as stated',
    issue_date: 'Issue date as stated',
    services_covered: 'Services covered as stated',
    services_text: 'Services as the vendor wrote them',
    services_provided: 'Services provided on your organization’s behalf, as recorded',
    states_end_date: 'The certificate states an end date',
    end_date: 'End date as stated',
    end_date_note: 'ComplianceOne computes nothing from this date.',
    declared_authority: 'Issuing authority named by the installed framework',
    note: 'Note',
    correction_note: 'Why this entry is corrected',
    correction_note_required: 'Say why the entry is corrected.',
    corrects: 'Corrects the entry of {date}',
    corrected: 'Corrected by a later entry',
    entry_line: 'Recorded by {name} from what the vendor stated or showed — ComplianceOne has not verified it',
    services_not_stated: 'Record which listed services this vendor provides on your behalf first',
    part_hidden: 'The vendor’s certificate is recorded here once this statement names a listed service.',
    error_required: 'Fill in this field.',
    error_future: 'This date is later than today.',
    error_end_before_issue: 'The end date is earlier than the issue date.',
    conflict: 'Someone changed this record since you opened it. It has been read again and your entries are kept: review them and save again.',
    save_failed: 'This was not recorded.',
    evidence: 'Evidence: {count}',
    evidence_legend: 'Evidence',
    evidence_hint: 'Files the vendor gave or showed, uploaded on this vendor. ComplianceOne keeps each file as uploaded and does not read it.',
    evidence_none: 'No file added.',
    evidence_upload: 'Add a file',
    evidence_remove: 'Remove',
    evidence_remove_label: 'Remove {filename}',
    evidence_remove_failed: 'The file could not be removed.',
    evidence_limit: 'At most {max} files can be added.',
    evidence_not_held: 'This file is no longer held.',
    evidence_not_held_error: 'A file is no longer held on this vendor. Remove it and add it again.',
    evidence_download_label: 'Download {filename}',
    close: 'Close',
};
export const enDeploymentPrivacy = {
    audience_kinds: {
        public: 'The public',
        data_subject_group: 'Named data subjects',
    },
    channels: {
        website: 'Website',
        point_of_collection: 'At the point of collection',
        employment_documentation: 'Employment documentation',
        contract_or_terms: 'Contract or terms',
        in_app: 'In the app',
        email: 'Email',
        other: 'Other',
    },
    access_kinds: {
        view: 'View',
        extract: 'Extract',
        export: 'Export',
        delete: 'Delete',
        configure: 'Configure',
    },
    export: {
        coverage_heading: 'Privacy notices by data subject',
        coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
        col_activity: 'Processing activity',
        col_data_subject: 'Data subject',
        col_notice: 'Notice',
        col_revision: 'Revision',
        col_channels: 'Channels',
        col_effective_from: 'In effect from',
        col_status: 'Status',
        not_recorded: 'Not recorded',
        no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
        status_replaced: '{status}, replaced by a newer revision',
        not_stated: 'Not stated',
        access_heading: 'Who may access the data',
        col_version: 'Version',
        col_reviewed_on: 'Reviewed on',
        col_role_or_position: 'Role or position',
        col_access_kinds: 'Permitted actions',
        col_means: 'By what means',
        col_conditions: 'On what conditions',
        no_access_statement: 'No access statement recorded',
        no_data_subjects: 'No data subjects recorded',
        public_linked: 'Also linked: {count} public notices, which name no data subject',
        public_linked_one: 'Also linked: 1 public notice, which names no data subject',
        coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
        access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
        col_approved_by: 'Approved by',
        col_systems: 'Systems',
    },
    notice_form: {
        heading: 'Audience, channels and deployment',
        intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
        audience_label: 'Whom the notice is given to',
        audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
        audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
        audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
        data_subjects_label: 'Data subjects the notice is given to',
        data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
        data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
        data_subjects_none: 'The linked processing activities record no data subjects.',
        data_subjects_required: 'Choose at least one data subject.',
        data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
        channels_label: 'How the audience is given the notice',
        channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
        channels_required: 'Choose at least one channel.',
        channel_note_label: 'Describe the other channel',
        channel_note_required: 'Describe the other channel.',
        effective_from_label: 'In effect from',
        effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
        activities_label: 'Processing activities the notice is given for',
        activities_hint: 'The data subjects of these activities are the ones the notice can name.',
        systems_label: 'Data-map systems the notice covers',
        links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
        systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
        picker_loading: 'Loading the list…',
        activities_empty: 'No processing activity is recorded.',
        systems_empty: 'No data-map system is recorded.',
        activities_failed: 'The processing activities could not be read.',
        systems_failed: 'The data-map systems could not be read.',
        activities_manual_label: 'Processing activity IDs',
        systems_manual_label: 'Data-map system IDs',
        manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
        off_list: '{value} (not in the list read)',
        retry: 'Reload',
        reloading: 'Reloading…',
        activities_retry_subject: 'the processing activities',
        systems_retry_subject: 'the data-map systems',
        error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
        error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
        error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
        col_audience: 'Audience',
        col_channels: 'Channels',
        col_effective_from: 'In effect from',
        col_links: 'Linked to',
        links_activities: 'Processing activities: {names}',
        links_activities_count: '{count} processing activities',
        links_activities_count_one: '1 processing activity',
        links_systems: 'Systems: {names}',
        links_systems_count: '{count} systems',
        links_systems_count_one: '1 system',
        links_none: 'None',
        other_channel: 'Other: {note}',
        data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
        error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
        audience_with_subjects: '{label}: {subjects}',
    },
    coverage: {
        heading: 'Notices',
        intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
        not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
        no_subjects: 'This activity records no data subjects.',
        list_label: 'Notices by data subject',
        notice_line: '{title}, revision {revision}',
        notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
        load_failed: 'The notices could not be read.',
        retry_subject: 'the notices of this activity',
        pagination: 'Pages of data subjects',
        public_heading: 'Also linked: public notices',
        public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
        public_failed: 'The public notices linked to this activity could not be read.',
        public_retry_subject: 'the public notices',
        truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
        subject_state: '{subject}: {state}',
    },
    access: {
        intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
        record: 'Record a revision',
        record_first: 'Record the access statement',
        current: 'Current',
        version: 'Version {version}',
        reviewed: 'Reviewed on {date}',
        approved: 'Approved by {name}',
        not_approved: 'No approver recorded',
        recorded: 'Recorded by {name} on {date}',
        systems: 'Systems: {names}',
        entries_label: 'Entries of version {version}',
        evidence_count: 'Documents: {count}',
        evidence_none: 'No document attached',
        evidence_open: 'Documents',
        evidence_open_aria: 'Documents of version {version}',
        load_failed: 'The access statement could not be read.',
        retry_subject: 'the access statement',
        pagination: 'Pages of access statement revisions',
        history_label: 'Revisions of the access statement',
        unknown_person: 'Unknown person',
    },
    access_form: {
        title: 'Record a revision of the access statement',
        replaces: 'This revision replaces version {version}. The earlier versions are kept.',
        first: 'This is the first revision of the access statement for this activity.',
        entries_heading: 'Who may access the data',
        entry_legend: 'Entry {n}',
        role_label: 'Role or position',
        role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
        access_kinds_label: 'Permitted actions',
        means_label: 'By what means',
        means_hint: 'For example, the extraction software named, a console, or a request to IT.',
        conditions_label: 'On what conditions',
        conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
        add_entry: 'Add an entry',
        remove_entry: 'Remove entry {n}',
        reviewed_on_label: 'Reviewed on',
        reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
        approver_label: 'Approved by (optional)',
        approver_none: 'No approver',
        approver_retry_subject: 'the list of people',
        systems_label: 'Data-map systems the statement covers',
        systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
        missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
        submit: 'Record the revision',
        saving: 'Recording…',
        recorded: 'Revision recorded.',
        record_failed: 'The revision could not be recorded. What you entered is kept.',
        conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
        conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
    },
    access_files: {
        heading: 'Documents',
        intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
        slot: 'Documents of this revision',
        none: 'No document is attached to this revision.',
        file_hash: 'SHA-256: {hash}',
        not_held: 'Document no longer held',
        download: 'Download',
        remove: 'Remove',
        upload: 'Add a document',
        bound: 'Document added.',
        removed: 'Document removed.',
        bind_failed: 'The document could not be added.',
        remove_failed: 'The document could not be removed.',
        not_owned: 'The revision this document belongs to was not found.',
        remove_confirm: 'Remove this document from the revision and delete it?',
        load_failed: 'The documents could not be loaded.',
    },
};
// Sprint 62.58.0 PR 4 — R-57: authored English and structural parity for deferred locales.
export const enVehicleMonitoring = {
    title: 'Vehicle Monitoring Devices',
    description: 'Record devices installed in vehicles and the evidence from operating them.',
    empty_declaration: 'No installed framework declares a vehicle monitoring device register.',
    empty_devices: 'No devices match these filters.',
    load_failed: 'Could not load the vehicle monitoring register.',
    save_failed: 'Could not save the device.',
    changed: 'The device changed while you were editing. Check the latest values before saving again.',
    total: 'Devices',
    new_device: 'Record device',
    edit_device: 'Edit device',
    device: 'Device',
    lifecycle: 'Lifecycle',
    phase: 'Phase deadline',
    transition: 'Installation timing',
    filters: 'Filter devices',
    all: 'All',
    back: 'Back to devices',
    in_preparation: 'In preparation',
    in_use: 'In use',
    withdrawn: 'Withdrawn',
    installed_before_anchor: 'Installed before the effective date',
    on_or_after_anchor: 'Installed on or after the effective date',
    not_determined: 'Not determined',
    events: 'Device events',
    no_events: 'No device events recorded.',
    events_failed: 'Could not load the device events.',
    new_event: 'Record device event',
    event_kind: 'Event kind',
    event_save_failed: 'Could not record the device event.',
    export_failed: 'Could not download the device export.',
    export: 'Export devices',
    retention_observation: 'Retention observation',
    device_fault: 'Device fault',
    data_extraction: 'Data extraction',
    authority_handover: 'Authority handover',
    observed_days: 'Observed days',
    minimum_days: 'Minimum days',
    below_minimum: 'Below minimum',
    device_code: 'Integrity code supplied by the device',
    checksum: 'Stored file checksum',
    integrity_matches: 'The values match.',
    integrity_differs: 'The values differ.',
    no_incident: 'No incident linked.',
    open_incident: 'Open an incident',
    link_incident: 'Link an existing incident',
    link_evidence: 'Link an evidence record',
    correct_event: 'Record a correction',
    corrects_event: 'Corrects the event recorded on',
    recorded_on: 'Recorded on',
    prior_outputs: 'Recorded before the register',
    prior_outputs_empty: 'No saved outputs from the prior form record.',
    prior_outputs_failed: 'Could not read the prior form outputs.',
    effective_date: 'Effective date',
    upload_copy: 'Upload the evidence copy',
    handover: 'Related authority handover',
    replaced_fields: 'Declared by a draft that has been replaced by {name}. Re-read these fields against the final text.',
    draft_fields: 'Draft-sourced fields',
    phase_not_declared: 'No phase deadline declared for this device and vehicle category.',
};
export const enRecordLinks = {
    heading: 'Linked records',
    empty: 'No linked records.',
    restricted: 'Restricted record',
    changed: 'Changed since linking',
    review: 'Mark as reviewed',
    review_failed: 'Could not mark this link as reviewed. Check the latest record and try again.',
    load_failed: 'Could not load linked records.',
    boundary: 'Conformity and processing',
    retry: 'Retry',
    previous: 'Previous page',
    next: 'Next page',
    add: 'Link a record',
    kind: 'Record kind',
    save_failed: 'Could not link the record.',
    kinds: {
        form_instance: 'Form record',
        vehicle_monitoring_device: 'Vehicle monitoring device',
        vehicle_monitoring_device_event: 'Device event',
        vendor_questionnaire: 'Vendor questionnaire',
        vendor: 'Vendor',
        ai_system: 'AI system',
        ai_system_version: 'AI system version',
        ai_classification_run: 'AI classification run',
        processing_activity: 'Processing activity',
        dpia: 'DPIA',
        incident: 'Incident',
        evidence: 'Evidence',
        data_map_system: 'System',
    },
};
export const enQuestionnaireSeed = {
    start: 'Start from an installed framework template',
    select: 'Installed framework template',
    empty: 'No installed framework declares a questionnaire template.',
    load_failed: 'Could not load installed framework templates.',
    create_failed: 'Could not copy the framework template.',
    changed: 'The framework template changed since you started from it.',
    compare: 'Compare with the installed framework template',
    compare_failed: 'Could not compare the framework template.',
    unchanged: 'The installed framework template is unchanged.',
    supplier_role: 'Supplier role',
    all_roles: 'All supplier roles',
    device_models: 'Device models',
    initiation_details: 'Request details',
    source_boundary: 'Supplier evidence and conformity',
};
//# sourceMappingURL=en-extras.js.map